import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import WrappingSelect from "./WrappingSelect";
import { useLocation, useNavigate } from "react-router-dom";
import UploadDocumentsModal from "./UploadDocumentsModal";
import {
  validateForm,
  normalizeProposalAddressFields,
  resolveAddressLinesFromSources,
  packAddressLines,
  isAutofilledNameLocked,
  isPersonNameOverLimit,
  cleanPersonNamePreservingOverLimit,
  personNameInputMaxLength,
} from "../../common/utils";
import { journeyActions } from "../../store/journeySlice";
import { validateBankAccountAction } from "../../api/bankActions";
import BrandLoader from "../../common/components/BrandLoader";
import {
  resolveSlotLaPayload,
  FALLBACK_PR_BY_SLOT,
} from "../../config/documentLaCodes";
import { uploadDocumentAction } from "../../api/documentActions";
import { serializeDocumentFileMetaWithImagePreview } from "../../common/utils/documentFileMeta";
import { getDocumentUploadOverSizeError } from "../../common/utils/documentUploadLimits";
import { isProposalDocumentSlotFilled } from "../../common/utils/proposalDocumentSlots";
import { mergeProposerStagesFromJourney } from "../../common/utils/proposalCumulativeHelpers";
import {
  DOCUMENT_UPLOAD_TEXT,
  proposerParagraphText,
  proposerHeading,
  MARITAL_STATUS_OPTIONS,
  RESIDENTIAL_STATUS_OPTIONS,
  SALUTATION_GENDER_MAP,
  PHOTOGRAPH_OPTIONS,
  TEXT_CONTENT,
  SIDE_STEPS_CONFIG,
} from "../../constants";
import { logError } from "../../common/utils/logger";
import { isGenderRestricted } from "../../common/utils/genderAgeRestriction";
import ToggleSwitch from "../../common/components/Toggle";
import {
  DiscountBanner,
  SideProgress,
} from "../../common/components/SideProgressBar";
import {
  buildIdOptions,
  buildOccupationTypeOptions,
  resolveBizCode,
  resolveLabel,
  resolveLabelAndBizCode,
  resolveStaticLabel,
} from "../../config/masterBizCodes";
/* ─── Side‑step configuration ─── */

const BOOLEAN_TOGGLE_FIELDS = new Set([
  "communicationAddressSameAsPermanent",
  "taxResidentialOutsideIndia",
  "criminalCharges",
  "politicallyExposed",
  "existingPolicies",
  "otherApplication",
  "bankVerified",
  "nachAccepted",
]);

const normalizeBooleanField = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }
  return !!value;
};

const normalizeProposalBooleanFields = (data) => {
  if (!data || typeof data !== "object") return data;
  const next = { ...data };
  BOOLEAN_TOGGLE_FIELDS.forEach((key) => {
    if (key in next) next[key] = normalizeBooleanField(next[key]);
  });
  return next;
};

const normalizeLocationValue = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const _findMatchingCity = (cities = [], rawCityValues = []) => {
  const normalizedCandidates = rawCityValues
    .map(normalizeLocationValue)
    .filter(Boolean);

  if (normalizedCandidates.length === 0) {
    return null;
  }

  const exactMatch = cities.find((city) => {
    const normalizedCity = normalizeLocationValue(city?.cityName);
    return normalizedCandidates.some(
      (candidate) => candidate === normalizedCity,
    );
  });

  if (exactMatch) {
    return exactMatch;
  }

  return cities.find((city) => {
    const normalizedCity = normalizeLocationValue(city?.cityName);
    return normalizedCandidates.some(
      (candidate) =>
        candidate.includes(normalizedCity) ||
        normalizedCity.includes(candidate),
    );
  });
};

const resolveAadhaarLocation = (address = {}, pincodeData = []) => {
  const rawState = address?.state || "";

  const rawCityValues = [address?.vtc, address?.dist, address?.subdist].filter(
    Boolean,
  );

  const normalize = (val) => (val || "").toLowerCase().replace(/\s+/g, "");

  // 1. Match State
  const matchedState = pincodeData.find(
    (item) => normalize(item.state) === normalize(rawState),
  )?.state;

  // 2. Get cities under matched state (if exists)
  const scopedData = matchedState
    ? pincodeData.filter((item) => item.state === matchedState)
    : pincodeData;

  // 3. Match City from Aadhaar fields
  let matchedCity = "";

  for (let val of rawCityValues) {
    const match = scopedData.find(
      (item) => normalize(item.city) === normalize(val),
    );
    if (match) {
      matchedCity = match.city;
      break;
    }
  }

  // 4. Fallback: derive state from city if state not matched
  let derivedState = matchedState;

  if (!derivedState && matchedCity) {
    const match = pincodeData.find((item) => item.city === matchedCity);
    derivedState = match?.state || "";
  }

  return {
    resolvedState: derivedState || "",
    resolvedCity: matchedCity || "",
    hasLockedState: Boolean(rawState && derivedState),
    hasLockedCity: Boolean(rawCityValues.length && matchedCity),
    hasRawState: Boolean(rawState),
    hasRawCity: rawCityValues.length > 0,
  };
};

/* ─── Illustration SVG for left panel ─── */
export const LeftIllustration = () => (
  <div className="proposal-side__illustration">
    <svg
      width="200"
      height="150"
      viewBox="0 0 200 150"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M26.3286 67.3369L26.3298 67.3422L45.1451 64.125L69.0405 71.1946L79.2462 67.4292C80.6677 67.7813 81.94 68.1347 83.0907 68.4909V68.4905C83.0907 68.4905 119.945 74.3793 115.507 106.994L94.9109 107.488C94.3389 109.257 94.298 108.829 93.8837 109.886L86.5072 134.27H26.1437L25.583 114.376L7.26435 126.165C5.00099 127.622 1.99641 126.161 1.75054 123.481C0.974426 115.014 0.0390778 100.114 0.0250397 99.7279C-0.940384 72.6265 26.3286 67.3369 26.3286 67.3369Z"
        fill="#E35055"
      />
      <path
        d="M92.9457 113.389C93.1242 113.389 93.2875 113.269 93.334 113.088L97.0036 98.7901C97.0585 98.5755 96.9294 98.3569 96.7148 98.3016C96.5002 98.247 96.2816 98.3758 96.2263 98.5904L92.5567 112.888C92.5017 113.103 92.6313 113.322 92.8455 113.377C92.8792 113.385 92.9125 113.389 92.9457 113.389Z"
        fill="#252527"
      />
      <path
        d="M22.4995 115.979C22.5849 115.979 22.6708 115.953 22.7434 115.896L33.9964 107.239C35.3765 106.326 36.279 104.833 36.4499 103.159L37.6098 91.7952C37.6323 91.5746 37.4719 91.3781 37.2513 91.3552C37.0347 91.3352 36.8341 91.4932 36.8113 91.7134L35.6513 103.077C35.5053 104.511 34.732 105.791 33.5299 106.586L22.2545 115.26C22.0788 115.395 22.0463 115.647 22.1806 115.822C22.2605 115.926 22.3792 115.979 22.4995 115.979Z"
        fill="#252527"
      />
      <path
        d="M53.2738 29.359L58.8354 29.1657C61.5564 29.0711 63.9649 30.6506 65.0679 32.9721L63.991 17.1843C63.991 17.1843 69.555 20.4067 76.8572 16.1876C76.8572 16.1876 85.2557 13.402 87.886 19.8969C87.8925 19.9615 87.9013 20.026 87.9057 20.091L89.5221 43.5605C90.1133 52.1459 84.2485 59.6346 76.1882 60.9799L74.3103 70.2993C72.9245 77.1764 66.0739 81.651 59.0865 80.2431L58.7965 80.1846C51.8091 78.7767 45.838 73.4956 47.2238 66.6184L52.7179 43.8048L51.43 43.8493C47.4472 43.9877 44.0748 40.8423 43.936 36.8595L43.9176 36.328C43.8486 34.3474 44.6704 32.5365 46.0157 31.2694C47.1496 30.2017 48.6537 29.5187 50.3234 29.4609L53.2738 29.359Z"
        fill="#FFAA89"
      />
      <path
        d="M76.069 61.2553C76.069 61.2553 61.9734 59.5787 60.4605 54.2101C60.4605 54.2101 58.5569 61.7001 74.7598 68.4725L76.069 61.2553Z"
        fill="#FF6A48"
      />
      <path
        d="M51.6214 48.3587C51.6214 48.3587 51.6315 46.2666 50.5557 43.9118L56.6764 43.6992L51.6214 48.3587Z"
        fill="#252527"
      />
      <path
        d="M52.8626 12.8661C52.8626 12.8661 49.0487 6.21318 59.2496 1.4097C69.4502 -3.39377 73.2056 5.61916 73.2056 5.61916C73.2056 5.61916 74.4229 -2.63691 86.7673 1.36237C99.1121 5.36126 88.1583 20.6621 88.1583 20.6621C88.0769 20.3926 87.9842 20.1403 87.8856 19.8972C85.2552 13.4019 76.8567 16.1879 76.8567 16.1879C69.5541 20.407 63.9905 17.1846 63.9905 17.1846L64.9467 31.2028C65.3611 34.0052 65.9126 37.7137 66.3076 40.3661C66.5623 42.0772 68.3492 43.0976 69.9544 42.451L78.7888 38.8917C79.2103 38.722 79.668 38.6623 80.1188 38.718L87.0994 39.5848C88.3613 39.7416 89.3383 40.764 89.4378 42.0315L89.9628 48.7233C90.5448 56.139 84.5926 62.4342 77.156 62.2685L73.2598 62.1815C72.9946 62.1755 72.7315 62.1301 72.4796 62.0471L71.5006 61.991C61.4953 61.4154 55.1629 51.0019 59.2556 41.8534C59.6002 41.3713 59.7755 40.7881 59.753 40.1957L59.3475 29.9694C59.3367 29.6939 59.3688 29.4268 59.4377 29.1749C59.2384 29.1636 59.0379 29.1584 58.8349 29.1656L53.2734 29.359L50.3233 29.4612C48.654 29.5194 47.1495 30.2021 46.0156 31.2698C44.2492 26.8357 42.7062 22.4626 42.2566 19.746C41.0393 12.3968 52.8626 12.8661 52.8626 12.8661Z"
        fill="#252527"
      />
      <path
        d="M49.6976 39.0343C49.773 39.0343 49.8488 39.0134 49.9162 38.9693L52.2425 37.4524C52.3408 37.3882 52.4058 37.2847 52.421 37.1684C52.4363 37.0521 52.4002 36.9354 52.3219 36.8483L49.8356 34.082C49.6864 33.9176 49.4333 33.9039 49.2688 34.0519C49.1044 34.1999 49.0908 34.4538 49.2388 34.6183L51.4123 37.0364L49.4786 38.2975C49.2929 38.4186 49.2404 38.6669 49.3615 38.8526C49.4385 38.9705 49.5664 39.0343 49.6976 39.0343Z"
        fill="#FF6A48"
      />
      <path
        d="M86.9417 31.7686C87.1157 32.5848 86.5951 33.3874 85.7793 33.5614C84.9631 33.7355 84.1605 33.2149 83.9864 32.3987C83.8124 31.5825 84.333 30.7799 85.1492 30.6058C85.965 30.4317 86.7676 30.9523 86.9417 31.7686Z"
        fill="#252527"
      />
      <path
        d="M76.0943 32.0973C76.0863 32.932 75.4036 33.6018 74.5689 33.5942C73.7347 33.5861 73.0644 32.9035 73.0725 32.0688C73.0805 31.2345 73.7631 30.5643 74.5974 30.5723C75.4321 30.58 76.1023 31.2626 76.0943 32.0973Z"
        fill="#252527"
      />
      <path
        d="M73.1326 33.7669C73.1326 33.7669 74.4153 32.7104 75.7975 33.7785L73.1326 33.7669Z"
        fill="#FFA386"
      />
      <path
        d="M75.7963 34.1799C75.9167 34.1799 76.0354 34.1262 76.114 34.0239C76.2496 33.8486 76.2175 33.5968 76.0418 33.4612C74.7483 32.4621 73.4062 33.0216 72.8768 33.4576C72.7055 33.5984 72.6815 33.8515 72.8222 34.0223C72.9622 34.1924 73.2141 34.2176 73.385 34.0785C73.4283 34.0436 74.4483 33.2442 75.5509 34.0961C75.6247 34.1527 75.7109 34.1799 75.7963 34.1799Z"
        fill="#FF6A48"
      />
      <path
        d="M86.8692 34.5304C86.9767 34.5304 87.0846 34.4871 87.1636 34.4017C87.314 34.2388 87.304 33.9849 87.1411 33.8349C85.9583 32.742 84.6503 33.2285 84.1241 33.638C83.9496 33.774 83.9179 34.0259 84.0539 34.2007C84.1899 34.3752 84.4406 34.4069 84.615 34.2721C84.7185 34.1935 85.6422 33.5421 86.5964 34.4237C86.6739 34.4955 86.7717 34.5304 86.8692 34.5304Z"
        fill="#FF6A48"
      />
      <path
        d="M86.6414 36.9201C89.4362 36.9201 91.7096 34.3166 91.7096 31.1163C91.7096 27.916 89.4362 25.3125 86.6414 25.3125C83.8466 25.3125 81.5732 27.916 81.5732 31.1163C81.5736 34.3166 83.847 36.9201 86.6414 36.9201ZM86.6414 26.1151C88.9934 26.1151 90.9074 28.3588 90.9074 31.1167C90.9074 33.8746 88.9938 36.1183 86.6414 36.1183C84.2894 36.1183 82.3754 33.8746 82.3754 31.1167C82.3758 28.3584 84.2894 26.1151 86.6414 26.1151Z"
        fill="white"
      />
      <path
        d="M80.653 28.6172V33.8651H83.3804C84.0811 33.8651 84.649 34.433 84.649 35.1337V35.7077C84.649 37.0798 83.5364 38.1924 82.1643 38.1924H79.6795"
        fill="#FFAA89"
      />
      <path
        d="M79.6788 38.5946H82.1636C83.7551 38.5946 85.0494 37.2999 85.0494 35.7088V35.1348C85.0494 34.2139 84.3006 33.4647 83.3797 33.4647H81.053V28.6179C81.053 28.3965 80.8737 28.2168 80.6519 28.2168C80.4301 28.2168 80.2508 28.3965 80.2508 28.6179V34.2669H83.3797C83.8578 34.2669 84.2473 34.6563 84.2473 35.1348V35.7088C84.2473 36.8579 83.3123 37.7925 82.1636 37.7925H79.6788C79.457 37.7925 79.2777 37.9721 79.2777 38.1935C79.2777 38.4149 79.457 38.5946 79.6788 38.5946Z"
        fill="#FF6A48"
      />
      <path
        d="M74.4129 36.5529C77.2077 36.5529 79.4811 33.9494 79.4811 30.7491C79.4811 27.5488 77.2077 24.9453 74.4129 24.9453C71.6181 24.9453 69.3443 27.5488 69.3443 30.7491C69.3443 33.9494 71.6181 36.5529 74.4129 36.5529ZM74.4129 25.7475C76.7649 25.7475 78.6789 27.9912 78.6789 30.7491C78.6789 33.507 76.7653 35.7507 74.4129 35.7507C72.0605 35.7507 70.1465 33.507 70.1465 30.7491C70.1465 27.9912 72.0605 25.7475 74.4129 25.7475Z"
        fill="white"
      />
      <path
        d="M70.3415 25.3321C70.8276 25.4071 71.3795 25.2054 71.9238 25.0409C72.4685 24.8641 73.02 24.6996 73.5041 24.5741C73.9894 24.4477 74.5514 24.3218 75.1133 24.2107C75.6688 24.0884 76.252 23.9945 76.6338 23.6937C77.004 23.4093 77.0421 22.861 76.388 22.3693C75.7679 21.8864 74.3865 21.545 72.7802 21.959C71.1778 22.3801 70.1578 23.3467 69.8702 24.0659C69.559 24.8099 69.8742 25.266 70.3415 25.3321Z"
        fill="#252527"
      />
      <path
        d="M88.7986 23.7011C88.4332 23.8467 87.9807 23.7605 87.5387 23.7059C87.0947 23.6402 86.6475 23.5868 86.2572 23.5563C85.8662 23.5254 85.4158 23.5074 84.9673 23.5022C84.5225 23.4861 84.0601 23.4998 83.7255 23.2988C83.4023 23.1103 83.3004 22.6354 83.7404 22.0963C84.1551 21.5705 85.1759 21.0439 86.4706 21.143C87.7629 21.2488 88.6774 21.9291 88.9947 22.5127C89.3336 23.1139 89.1507 23.566 88.7986 23.7011Z"
        fill="#252527"
      />
      <path
        d="M79.0796 30.6655H81.1464C81.3682 30.6655 81.5475 30.4858 81.5475 30.2644C81.5475 30.043 81.3682 29.8633 81.1464 29.8633H79.0796C78.8578 29.8633 78.6785 30.043 78.6785 30.2644C78.6785 30.4858 78.8578 30.6655 79.0796 30.6655Z"
        fill="white"
      />
      <path
        d="M69.7465 30.4668C69.9511 30.4668 70.1256 30.3112 70.1452 30.1034C70.1665 29.8828 70.004 29.6871 69.7838 29.6666L57.504 28.5075C57.2838 28.4878 57.0881 28.6487 57.0672 28.8693C57.046 29.0899 57.2084 29.2856 57.4286 29.3061L69.7084 30.4652C69.7213 30.4664 69.7341 30.4668 69.7465 30.4668Z"
        fill="white"
      />
      <path
        d="M76.4306 45.6851C76.4462 45.6851 76.4615 45.6852 76.4771 45.6848L81.2922 45.6234C81.514 45.6206 81.6909 45.4389 81.6881 45.2171C81.6853 44.9973 81.5064 44.8212 81.287 44.8212C81.2854 44.8212 81.2834 44.8212 81.2818 44.8212L76.4667 44.8826C75.1238 44.9002 73.9165 43.9597 73.6093 42.6465L72.8284 42.8294C73.2174 44.4911 74.7279 45.6851 76.4306 45.6851Z"
        fill="#FF6A48"
      />
      <path
        d="M75.9947 62.875L82.1647 68.2123L81.2438 80.8258C81.1968 81.4696 80.4316 81.7796 79.9498 81.3505L68.6014 71.239L75.9947 62.875Z"
        fill="white"
      />
      <path
        d="M48.9299 59.5332L69.11 71.1773L61.0075 84.7839C60.6321 85.4144 59.7806 85.5568 59.2207 85.0823L39.5896 68.4595L48.9299 59.5332Z"
        fill="white"
      />
      <path
        d="M78.4108 79.9783L73.0722 90.8351L61.5064 83.9471L68.9101 71.5137L78.4108 79.9783Z"
        fill="#252527"
      />
      <path
        d="M140.39 24.3176L152.878 24.405L169.182 24.4042L169.145 18.3577C169.14 17.5459 169.211 16.751 169.351 15.9805C171.528 4.02874 189.025 3.95855 191.328 15.8866C191.48 16.6736 191.56 17.4866 191.56 18.318V25.5882H191.552L191.522 31.0082C191.491 36.737 195.188 41.6351 199.987 42.7C200.105 42.7261 199.456 42.7385 198.831 42.7549C197.95 42.7782 197.25 42.7573 197.25 42.7573L150.674 42.8179C144.938 42.7778 140.317 37.4906 140.352 31.0082L140.39 24.3176Z"
        fill="#F8D3D3"
      />
      <path
        d="M102.518 24.2314C102.518 16.8084 107.202 10.4812 113.775 8.03935C115.363 7.31418 117.125 6.90226 118.984 6.89062L157.703 6.93515L157.7 6.92993H178.806C179.088 6.92993 179.367 6.94236 179.645 6.96041L180.364 6.96122V6.98488C180.31 6.9913 180.258 7.00093 180.204 7.00774C174.201 7.80511 169.529 12.7694 169.17 18.8853H169.177V139.227C169.177 145.069 164.389 149.806 158.484 149.806H102.54V101.253H102.518V24.2314H102.518Z"
        fill="white"
      />
      <path
        d="M112.206 71.8217H157.635C157.856 71.8217 158.036 71.6424 158.036 71.4206C158.036 71.1992 157.856 71.0195 157.635 71.0195H112.206C111.984 71.0195 111.805 71.1992 111.805 71.4206C111.805 71.642 111.984 71.8217 112.206 71.8217Z"
        fill="#252527"
      />
      <path
        d="M125.898 65.4037H145.035C145.257 65.4037 145.436 65.2241 145.436 65.0027C145.436 64.7813 145.257 64.6016 145.035 64.6016H125.898C125.676 64.6016 125.497 64.7813 125.497 65.0027C125.497 65.2241 125.676 65.4037 125.898 65.4037Z"
        fill="#252527"
      />
      <path
        d="M112.206 81.2416H157.635C157.856 81.2416 158.036 81.0623 158.036 80.8405C158.036 80.6187 157.856 80.4395 157.635 80.4395H112.206C111.984 80.4395 111.805 80.6187 111.805 80.8405C111.805 81.0623 111.984 81.2416 112.206 81.2416Z"
        fill="#252527"
      />
      <path
        d="M112.206 90.6596H157.635C157.856 90.6596 158.036 90.4803 158.036 90.2585C158.036 90.0367 157.856 89.8574 157.635 89.8574H112.206C111.984 89.8574 111.805 90.0367 111.805 90.2585C111.805 90.4803 111.984 90.6596 112.206 90.6596Z"
        fill="#252527"
      />
      <path
        d="M112.206 99.476H157.635C157.856 99.476 158.036 99.2967 158.036 99.0749C158.036 98.8531 157.856 98.6738 157.635 98.6738H112.206C111.984 98.6738 111.805 98.8531 111.805 99.0749C111.805 99.2963 111.984 99.476 112.206 99.476Z"
        fill="#252527"
      />
      <path
        d="M137.003 21.3139C137.546 19.6213 140.045 20.0172 140.038 21.7948C140.033 23.3073 141.97 23.9366 142.855 22.7101C143.894 21.2682 146.149 22.4169 145.593 24.1055C145.121 25.5418 146.769 26.7395 147.989 25.8462C149.423 24.7962 151.213 26.5854 150.162 28.0197C149.269 29.2399 150.466 30.8884 151.903 30.4155C153.592 29.86 154.74 32.1145 153.298 33.1541C152.071 34.0385 152.701 35.9762 154.214 35.9706C155.991 35.9638 156.387 38.4634 154.695 39.0061C153.255 39.4681 153.255 41.5057 154.695 41.9673C156.387 42.5104 155.991 45.0096 154.214 45.0028C152.701 44.9972 152.072 46.9348 153.298 47.8192C154.74 48.8585 153.592 51.1134 151.903 50.5575C150.467 50.0846 149.269 51.7331 150.162 52.9532C151.212 54.3875 149.423 56.1768 147.989 55.1263C146.769 54.2327 145.12 55.4303 145.593 56.8671C146.149 58.5557 143.894 59.7044 142.855 58.2625C141.97 57.0355 140.033 57.6652 140.038 59.1773C140.045 60.955 137.545 61.3509 137.003 59.6583C136.541 58.2183 134.503 58.2183 134.042 59.6583C133.498 61.3509 130.999 60.955 131.006 59.1773C131.012 57.6652 129.074 57.0355 128.19 58.2625C127.15 59.7044 124.895 58.5557 125.451 56.8671C125.924 55.4307 124.275 54.2327 123.055 55.1263C121.621 56.1764 119.832 54.3871 120.882 52.9532C121.776 51.7331 120.578 50.0846 119.141 50.5575C117.453 51.113 116.304 48.8585 117.746 47.8192C118.973 46.9348 118.343 44.9972 116.831 45.0028C115.053 45.0096 114.657 42.51 116.35 41.9673C117.79 41.5053 117.79 39.4677 116.35 39.0061C114.657 38.4634 115.053 35.9638 116.831 35.9706C118.343 35.9762 118.973 34.0385 117.746 33.1541C116.304 32.1149 117.453 29.86 119.141 30.4155C120.578 30.8884 121.775 29.2399 120.882 28.0197C119.832 26.5854 121.621 24.7962 123.055 25.8462C124.275 26.7399 125.924 25.5422 125.451 24.1055C124.895 22.4169 127.15 21.2682 128.19 22.7101C129.074 23.937 131.012 23.3073 131.006 21.7948C130.999 20.0172 133.499 19.6213 134.042 21.3139C134.503 22.7538 136.541 22.7538 137.003 21.3139Z"
        fill="#F6ABAE"
      />
      <path
        d="M128.371 52.7058H142.67C144.344 52.7058 145.706 51.3441 145.706 49.6703V38.3655C145.706 36.6918 144.344 35.3301 142.67 35.3301H128.371C126.697 35.3301 125.335 36.6918 125.335 38.3655V49.6703C125.335 51.3441 126.697 52.7058 128.371 52.7058ZM142.671 36.5334C143.681 36.5334 144.503 37.3552 144.503 38.3655V49.6703C144.503 50.6807 143.681 51.5025 142.671 51.5025H128.371C127.361 51.5025 126.539 50.6807 126.539 49.6703V38.3655C126.539 37.3552 127.361 36.5334 128.371 36.5334H142.671Z"
        fill="#252527"
      />
      <path
        d="M128.726 36.5331C129.058 36.5331 129.328 36.264 129.328 35.9315V32.6465C129.328 30.4526 131.352 28.6681 133.838 28.6681H137.205C139.692 28.6681 141.715 30.453 141.715 32.6465V35.9315C141.715 36.264 141.985 36.5331 142.317 36.5331C142.649 36.5331 142.919 36.264 142.919 35.9315V32.6465C142.919 29.7892 140.355 27.4648 137.205 27.4648H133.838C130.688 27.4648 128.125 29.7892 128.125 32.6465V35.9315C128.125 36.264 128.394 36.5331 128.726 36.5331Z"
        fill="#252527"
      />
      <path
        d="M131.89 42.2428C131.89 40.3762 133.495 38.8665 135.464 38.8949C137.323 38.9218 138.856 40.3461 138.926 42.1137C138.97 43.2339 138.435 44.2367 137.584 44.8744C137.401 45.012 137.289 45.2181 137.289 45.4403V46.3584C137.289 46.7519 136.954 47.0712 136.54 47.0712H134.279C133.865 47.0712 133.53 46.7519 133.53 46.3584V45.4411C133.53 45.2193 133.419 45.0124 133.236 44.8752C132.416 44.2619 131.89 43.3109 131.89 42.2428Z"
        fill="#252527"
      />
      <path
        d="M134.664 48.3331C134.664 48.7446 134.998 49.0783 135.41 49.0783C135.821 49.0783 136.155 48.7446 136.155 48.3331C136.155 47.9216 135.821 47.5879 135.41 47.5879C134.998 47.5879 134.664 47.9212 134.664 48.3331Z"
        fill="#252527"
      />
      <path
        d="M135.409 49.1793C135.875 49.1793 136.255 48.7999 136.255 48.3338C136.255 47.8673 135.875 47.4883 135.409 47.4883C134.943 47.4883 134.564 47.8677 134.564 48.3338C134.564 48.7999 134.943 49.1793 135.409 49.1793ZM135.409 47.6888C135.765 47.6888 136.054 47.9784 136.054 48.3338C136.054 48.6896 135.765 48.9787 135.409 48.9787C135.054 48.9787 134.764 48.6896 134.764 48.3338C134.764 47.978 135.054 47.6888 135.409 47.6888Z"
        fill="#F6ABAE"
      />
      <path
        d="M134.3 47.8861H136.519C136.668 47.8861 136.79 47.7646 136.79 47.615V47.5368C136.79 47.3872 136.668 47.2656 136.519 47.2656H134.3C134.15 47.2656 134.029 47.3872 134.029 47.5368V47.615C134.029 47.7646 134.15 47.8861 134.3 47.8861Z"
        fill="#252527"
      />
      <path
        d="M134.299 47.987H136.518C136.723 47.987 136.889 47.8202 136.889 47.6156V47.5374C136.889 47.3329 136.723 47.166 136.518 47.166H134.299C134.094 47.166 133.928 47.3325 133.928 47.5374V47.6156C133.928 47.8202 134.094 47.987 134.299 47.987ZM136.518 47.3666C136.612 47.3666 136.689 47.4432 136.689 47.5374V47.6156C136.689 47.7099 136.612 47.7865 136.518 47.7865H134.299C134.205 47.7865 134.128 47.7099 134.128 47.6156V47.5374C134.128 47.4432 134.205 47.3666 134.299 47.3666H136.518Z"
        fill="#F6ABAE"
      />
      <path
        d="M134.436 48.4274H136.385C136.515 48.4274 136.622 48.3212 136.622 48.1904V48.1218C136.622 47.9911 136.515 47.8848 136.385 47.8848H134.436C134.305 47.8848 134.199 47.9907 134.199 48.1218V48.1904C134.199 48.3212 134.305 48.4274 134.436 48.4274Z"
        fill="#252527"
      />
      <path
        d="M134.435 48.5284H136.384C136.57 48.5284 136.721 48.3772 136.721 48.1911V48.1225C136.721 47.9364 136.57 47.7852 136.384 47.7852H134.435C134.249 47.7852 134.098 47.9364 134.098 48.1225V48.1911C134.098 48.3772 134.249 48.5284 134.435 48.5284ZM136.384 47.9861C136.46 47.9861 136.521 48.0475 136.521 48.1229V48.1915C136.521 48.2669 136.46 48.3282 136.384 48.3282H134.435C134.36 48.3282 134.299 48.2669 134.299 48.1915V48.1229C134.299 48.0475 134.36 47.9861 134.435 47.9861H136.384Z"
        fill="#F6ABAE"
      />
      <path
        d="M137.848 43.0495C137.914 43.0495 137.974 43.0057 137.992 42.9388C138.432 41.336 137.161 40.4303 137.148 40.4211C137.08 40.3742 136.986 40.391 136.939 40.4588C136.891 40.527 136.908 40.6204 136.976 40.6682C137.021 40.6999 138.084 41.4655 137.703 42.8593C137.681 42.9396 137.728 43.0222 137.808 43.0442C137.821 43.0479 137.835 43.0495 137.848 43.0495Z"
        fill="#F6ABAE"
      />
      <path
        d="M154.653 26.3183C154.74 26.3183 154.828 26.2898 154.902 26.2308L158.687 23.2166C158.861 23.0787 158.889 22.8264 158.751 22.6531C158.613 22.4798 158.361 22.4514 158.188 22.5893L154.402 25.6035C154.229 25.7415 154.201 25.9938 154.339 26.1671C154.418 26.2665 154.535 26.3183 154.653 26.3183Z"
        fill="#252527"
      />
      <path
        d="M162.94 40.6669C163.122 40.6669 163.286 40.5422 163.33 40.3577C163.38 40.1419 163.246 39.9261 163.031 39.8756L158.321 38.7706C158.106 38.7196 157.889 38.8536 157.838 39.0694C157.788 39.2852 157.922 39.501 158.137 39.5515L162.848 40.6565C162.879 40.6637 162.909 40.6669 162.94 40.6669Z"
        fill="#252527"
      />
      <path
        d="M157.564 33.0443C157.6 33.0443 157.636 33.0395 157.672 33.0294L162.335 31.7407C162.549 31.6818 162.674 31.4608 162.615 31.2474C162.556 31.0336 162.334 30.9093 162.121 30.9674L157.458 32.2561C157.245 32.3151 157.119 32.5361 157.178 32.7495C157.227 32.9276 157.388 33.0443 157.564 33.0443Z"
        fill="#252527"
      />
      <path
        d="M113.334 40.31C113.541 40.31 113.716 40.1511 113.734 39.9413C113.751 39.7207 113.587 39.527 113.366 39.5094L108.544 39.1187C108.322 39.0995 108.13 39.2651 108.112 39.4861C108.094 39.7067 108.258 39.9004 108.479 39.9181L113.301 40.3087C113.312 40.3091 113.323 40.31 113.334 40.31Z"
        fill="#252527"
      />
      <path
        d="M113.944 33.0439C114.119 33.0439 114.281 32.9272 114.33 32.7495C114.389 32.5361 114.264 32.3151 114.05 32.2561L109.387 30.9674C109.175 30.9093 108.952 31.0336 108.893 31.2474C108.834 31.4608 108.96 31.6818 109.173 31.7407L113.836 33.0294C113.872 33.0391 113.908 33.0439 113.944 33.0439Z"
        fill="#252527"
      />
      <path
        d="M110.265 48.3069C110.328 48.3069 110.391 48.2924 110.45 48.2615L114.743 46.0307C114.94 45.9288 115.016 45.6865 114.915 45.49C114.812 45.2935 114.571 45.2161 114.373 45.3191L110.08 47.55C109.884 47.6519 109.807 47.8941 109.909 48.0907C109.981 48.2282 110.12 48.3069 110.265 48.3069Z"
        fill="#252527"
      />
      <path
        d="M115.848 26.5359C115.848 29.6223 118.333 32.2241 121.52 32.2241C124.689 32.2241 127.141 29.6551 127.141 26.5691C127.141 23.4831 124.673 20.8809 121.486 20.8809C118.317 20.8813 115.848 23.4334 115.848 26.5359ZM125.966 26.5715C125.966 29.0278 124.036 31.0525 121.523 31.0525C118.973 31.0525 117.023 28.9897 117.023 26.5338C117.023 24.0776 118.973 22.0528 121.485 22.0528C124.017 22.0528 125.966 24.1153 125.966 26.5715ZM119.626 25.4044C119.871 25.6996 120.309 25.7898 120.625 25.5712C120.857 25.4108 121.107 25.3181 121.336 25.3181C121.97 25.3181 122.437 25.8187 122.437 26.5194C122.437 27.2366 121.97 27.7371 121.336 27.7371C121.11 27.7371 120.847 27.6489 120.596 27.4953C120.305 27.3176 119.927 27.3753 119.694 27.6244L119.676 27.6441C119.37 27.9702 119.417 28.506 119.793 28.7479C120.295 29.0703 120.881 29.2548 121.436 29.2548C123.055 29.2548 124.222 28.121 124.222 26.5527C124.222 25.0013 123.021 23.867 121.37 23.867C120.835 23.867 120.265 24.0375 119.785 24.3246C119.41 24.5484 119.346 25.0691 119.626 25.4044Z"
        fill="#252527"
      />
      <path
        d="M122.008 118.476V136.2C122.008 143.216 116.32 148.903 109.305 148.903H106.946C98.5161 148.903 91.6819 142.069 91.6819 133.638C91.6819 127.704 86.8716 122.894 80.9374 122.894H76.519C75.5079 122.894 74.5368 123.06 73.6275 123.364V122.894H73.5237V116.229C73.5237 111.481 77.3733 107.631 82.1219 107.631H132.368V107.643C126.603 107.897 122.008 112.649 122.008 118.476Z"
        fill="#F4F2ED"
      />
      <path
        d="M14.6916 138.337L14.7842 131.493C14.7842 126.744 18.6339 122.895 23.3824 122.895H73.5237H73.6276V123.364C69.9985 124.574 67.381 127.996 67.381 132.032V136.35C67.381 143.781 61.3566 149.806 53.9252 149.806H5.12756C10.6261 148.724 14.6158 143.94 14.6916 138.337Z"
        fill="#FAF8F3"
      />
      <path
        d="M169.204 139.314C169.204 140.007 169.128 140.683 168.988 141.335C169.115 140.657 169.181 139.953 169.177 139.227L169.204 139.314Z"
        fill="#252527"
      />
      <path
        d="M67.382 136.349V132.032C67.382 127.996 69.9995 124.574 73.6286 123.364C74.5375 123.06 75.5089 122.894 76.5201 122.894H80.9385C86.8726 122.894 91.6829 127.704 91.6829 133.638C91.6829 142.069 98.5171 148.903 106.947 148.903H109.306C116.321 148.903 122.009 143.215 122.009 136.2V118.476C122.009 112.649 126.604 107.897 132.369 107.643C132.53 107.636 132.691 107.631 132.854 107.631C140.174 107.631 146.109 113.565 146.109 120.886V135.396C146.109 142.856 152.156 148.903 159.615 148.903C164.218 148.903 168.061 145.66 168.989 141.335C168.066 146.271 163.838 149.805 158.555 149.805H73.629H53.927C61.3576 149.805 67.382 143.78 67.382 136.349Z"
        fill="#F8D3D3"
      />
      <path
        d="M156.842 85.8682C158.742 81.7976 163.087 84.7873 165.919 86.2288C167.202 87.0118 168.384 87.7109 169.494 88.357C169.277 86.8293 169.064 85.2839 169.388 83.7445C169.492 83.2487 169.695 82.7068 170.138 82.4654C170.478 82.2797 170.889 82.3226 171.215 82.4866C171.541 82.6507 171.798 82.9206 172.043 83.1917C174.332 85.719 179.47 94.3846 179.47 94.3846C180.441 96.4655 181.099 98.7998 181.403 101.103C182.287 107.799 176.194 113.919 169.778 112.59C168.991 112.427 168.3 112.17 167.794 111.785L157.007 104.044C154.945 102.729 154.719 99.4432 156.636 99.4111C154.802 98.8648 153.03 97.5741 154.082 95.2839C154.524 93.9599 155.548 93.7645 156.64 93.935C154.978 93.3915 153.484 92.5239 154.22 90.5654C154.728 88.3442 156.527 88.2716 158.208 88.6711C157.122 88.1617 156.296 87.3302 156.842 85.8682Z"
        fill="#FFA386"
      />
      <path
        d="M174.125 91.4666C174.263 91.4666 174.397 91.3952 174.472 91.2672C174.583 91.0759 174.518 90.8305 174.327 90.719L168.497 87.3221C168.305 87.2106 168.059 87.2756 167.948 87.4669C167.837 87.6582 167.902 87.9037 168.093 88.0152L173.923 91.412C173.987 91.4489 174.056 91.4666 174.125 91.4666Z"
        fill="#FF6A48"
      />
      <path
        d="M162.416 91.4666C162.552 91.4666 162.684 91.3977 162.759 91.2729C162.874 91.0828 162.813 90.8365 162.623 90.7222C162.215 90.4768 158.622 88.3177 157.86 88.1099C157.649 88.0518 157.426 88.1773 157.368 88.3915C157.309 88.6049 157.436 88.8255 157.649 88.884C158.142 89.0184 160.707 90.504 162.208 91.4093C162.274 91.4482 162.345 91.4666 162.416 91.4666Z"
        fill="#FF6A48"
      />
      <path
        d="M160.248 96.53C160.386 96.53 160.521 96.4582 160.595 96.3298C160.706 96.1381 160.641 95.8926 160.449 95.7819L156.781 93.6577C156.589 93.5462 156.343 93.6124 156.233 93.8037C156.122 93.9955 156.187 94.2409 156.379 94.3516L160.047 96.4758C160.11 96.5127 160.179 96.53 160.248 96.53Z"
        fill="#FF6A48"
      />
      <path
        d="M159.156 101.276C159.297 101.276 159.434 101.201 159.507 101.068C159.614 100.874 159.543 100.63 159.349 100.523L157.348 99.4228C157.155 99.3162 156.91 99.3863 156.804 99.5813C156.697 99.775 156.768 100.019 156.962 100.126L158.963 101.226C159.024 101.26 159.09 101.276 159.156 101.276Z"
        fill="#FF6A48"
      />
      <path
        d="M83.6897 63.7106L81.2302 65.8203C80.6005 66.3602 80.5488 67.3324 81.1143 67.9918L83.3235 70.5676C83.8891 71.227 84.8577 71.3237 85.4874 70.7838L87.9473 68.6745C88.577 68.1346 88.6288 67.1624 88.0632 66.503L85.854 63.9271C85.2881 63.2674 84.3194 63.1707 83.6897 63.7106Z"
        fill="#FF6A48"
      />
      <path
        d="M86.8669 65.1214L109.678 91.7207L104.939 95.7854L82.1273 69.1861L86.8669 65.1214Z"
        fill="#F6ABAE"
      />
      <path
        d="M109.637 91.7632L104.985 95.7532C104.965 95.7705 104.893 95.8323 105.002 95.8218L112.315 99.5921L109.703 91.7905C109.693 91.7608 109.657 91.7459 109.637 91.7632Z"
        fill="#FFAA89"
      />
      <path
        d="M111.347 96.7977L109.682 98.226C109.675 98.232 109.649 98.2541 109.688 98.2505L112.305 99.6001L111.37 96.8077C111.367 96.7965 111.354 96.7913 111.347 96.7977Z"
        fill="#233863"
      />
      <path
        d="M88.554 74.0993C88.6001 74.0993 88.6467 74.0833 88.6844 74.0512C88.7686 73.979 88.7782 73.8523 88.7064 73.7684L85.4251 69.9428C85.3521 69.859 85.2258 69.8494 85.1423 69.9212C85.0581 69.9934 85.0485 70.1197 85.1203 70.2039L88.4016 74.0291C88.4417 74.0757 88.4975 74.0993 88.554 74.0993Z"
        fill="white"
      />
      <path
        d="M96.6146 79.6909C96.6607 79.6909 96.7072 79.6748 96.7449 79.6427C96.8292 79.5705 96.8388 79.4438 96.767 79.36L87.3068 68.3295C87.2338 68.2457 87.1071 68.2361 87.0241 68.3079C86.9398 68.3801 86.9302 68.5064 87.002 68.5907L96.4621 79.6211C96.5019 79.6668 96.558 79.6909 96.6146 79.6909Z"
        fill="white"
      />
      <path
        d="M26.0296 111.569L66.5037 84.7997L75.8857 77.4481C78.5108 75.3909 81.665 74.1191 84.9828 73.7794L95.7321 72.6784C97.2751 72.5203 98.8305 72.8143 100.209 73.5239L105.312 76.1502C106.126 76.5689 106.612 77.4309 106.55 78.3434L106.51 78.9418L106.902 79.1287C107.976 79.6405 108.658 80.725 108.655 81.9147L108.648 84.0256L109 84.8507C109.482 85.9818 109.268 87.2897 108.45 88.2078L100.528 97.0976C100.043 97.6411 99.1987 97.6571 98.6938 97.1325C97.4476 95.8378 97.3585 93.8183 98.486 92.4185L100.937 89.3754L92.7287 94.2342C91.3786 95.0336 89.8577 95.5005 88.2914 95.5963L83.2301 95.9068L77.997 101.881L46.6064 139.753C42.8454 143.795 38.0275 146.077 33.068 146.688C23.0707 147.92 13.5649 141.966 9.88526 132.589L7.01466 126.304L26.0296 111.569Z"
        fill="#FFAA89"
      />
      <path
        d="M106.377 79.5319C106.547 79.5319 106.704 79.4236 106.759 79.2527C106.826 79.0421 106.71 78.8159 106.499 78.7485L100.4 76.8024C100.19 76.7347 99.9633 76.8518 99.8959 77.0627C99.829 77.2733 99.9453 77.4995 100.156 77.5669L106.255 79.5134C106.295 79.5263 106.336 79.5319 106.377 79.5319Z"
        fill="#FF6A48"
      />
      <path
        d="M108.408 84.3683C108.587 84.3683 108.75 84.2476 108.797 84.0663C108.852 83.8513 108.722 83.6331 108.507 83.5786L100.927 81.6497C100.713 81.5936 100.494 81.7247 100.439 81.9393C100.384 82.1543 100.514 82.3725 100.729 82.427L108.308 84.3559C108.342 84.3643 108.375 84.3683 108.408 84.3683Z"
        fill="#FF6A48"
      />
      <path
        d="M100.937 89.7774C101.047 89.7774 101.157 89.7324 101.236 89.6438L102.764 87.9364C102.911 87.7711 102.897 87.5176 102.732 87.37C102.567 87.222 102.313 87.2361 102.166 87.4013L100.638 89.1087C100.491 89.274 100.505 89.5275 100.67 89.6751C100.746 89.7437 100.842 89.7774 100.937 89.7774Z"
        fill="#FF6A48"
      />
      <path
        d="M83.2309 96.3088C83.4451 96.3088 83.6232 96.1395 83.6312 95.9234C83.6396 95.702 83.4675 95.5155 83.2461 95.507L77.2695 95.2724C77.0605 95.2572 76.8616 95.4356 76.8531 95.6574C76.8447 95.8788 77.0168 96.0653 77.2382 96.0738L83.2148 96.3084C83.2201 96.3088 83.2257 96.3088 83.2309 96.3088Z"
        fill="#FF6A48"
      />
    </svg>
  </div>
);

/* ─── Form Field Components ─── */
const FormField = ({
  label,
  error,
  children,
  required,
  halfWidth,
  optional,
}) => (
  <div className={`proposal-field ${halfWidth ? "half" : ""}`}>
    <label className="proposal-field__label">
      {label}
      {optional && <span className="optional"> (Optional)</span>}
      {required && <span className="required"> *</span>}
    </label>
    {children}
    {error && <div className="proposal-field__error">{error}</div>}
  </div>
);

const TextInput = ({
  placeholder,
  value,
  onChange,
  name,
  disabled,
  maxLength,
  inputMode,
  onBlur,
  onFocus,
}) => {
  const capitalizeWords = (text) => {
    return text.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <input
      type="text"
      className="proposal-input"
      placeholder={placeholder || "Enter"}
      value={value || ""}
      onChange={(e) =>
        onChange(name, capitalizeWords(e.target.value))
      }
      onBlur={onBlur}
      onFocus={onFocus}
      disabled={disabled}
      maxLength={maxLength}
      inputMode={inputMode}
      autoComplete="new-password"
    />
  );
};

const SelectInput = ({
  placeholder: _placeholder,
  value,
  onChange,
  name,
  options,
  disabled,
}) => (
  <select
    className="proposal-input proposal-select"
    value={value || ""}
    onChange={(e) => onChange(name, e.target.value)}
    disabled={disabled}
  >
    <option value="">
      {name?.toLowerCase().includes("city")
        ? "Enter Pincode or Select State"
        : options?.length === 0
          ? "Loading..."
          : "Select"}
    </option>
    {(options || []).map((opt) => (
      <option
        key={typeof opt === "string" ? opt : opt.value}
        value={typeof opt === "string" ? opt : opt.value}
      >
        {typeof opt === "string" ? opt : opt.label}
      </option>
    ))}
  </select>
);

/* ═══════════════════════════════════════════════════
   PERSONAL DETAILS SECTION
   ═══════════════════════════════════════════════════ */

const PersonalDetails = ({
  formData,
  errors,
  onChange,
  onContinue,
  onUploadClick,
  documents = {},
  journeyType: _journeyType,
}) => {
  const apiCache = useSelector((state) => state.journey.apiCache || {});
  const leadData = useSelector((state) => state.journey.leadFormData);
  const leadGender = useSelector((state) => state.journey.stepData[0]?.gender);
  const allPincodeData = apiCache?.getProductPincodes?.data || [];
  const [isLocationLocked, setIsLocationLocked] = useState(false);

  const aadharData = useSelector(
    (state) => state.journey.customerVerificationData?.aadhaar?.response,
  );
  const aadharAddress = useSelector(
    (state) =>
      state.journey.customerVerificationData?.aadhaar?.response?.address,
  );
  const sameAddress = formData.communicationAddressSameAsPermanent;
  const taxResidentialOutsideIndia = formData.taxResidentialOutsideIndia;


  const isNameAutofilled = Boolean(
    String(aadharData?.full_name || "").trim() ||
    String(leadData?.fullName || "").trim(),
  );
  const isFatherFromAadhaar =
    (aadharData?.care_of || "").includes("S/O") ||
    (aadharData?.care_of || "").includes("D/O");
  const [nameEditUnlocked, setNameEditUnlocked] = useState(false);
  const [fatherEditUnlocked, setFatherEditUnlocked] = useState(false);
  useEffect(() => {
    if (isPersonNameOverLimit(formData.fullName)) setNameEditUnlocked(true);
  }, [formData.fullName]);
  useEffect(() => {
    if (isPersonNameOverLimit(formData.fatherName)) setFatherEditUnlocked(true);
  }, [formData.fatherName]);
  const isNameDisabled = isAutofilledNameLocked(
    isNameAutofilled,
    formData.fullName,
    nameEditUnlocked,
  );
  const isFatherNameDisabled = isAutofilledNameLocked(
    isFatherFromAadhaar,
    formData.fatherName,
    fatherEditUnlocked,
  );

  const [isFocused, setIsFocused] = useState(false);

  const aadhaar = useSelector(
    (state) => state.journey.customerVerificationData?.aadhaar,
  );
  const aadhaarData = aadhaar?.response;
  const isAadhaarVerified = !!aadhaarData;

  const hasUploaded = (docLabel) => {
    const proposerDocs = documents?.proposer || {};
    if (docLabel === "Photograph")
      return isProposalDocumentSlotFilled(proposerDocs.photograph);
    if (docLabel === "Age Proof")
      return isProposalDocumentSlotFilled(proposerDocs.ageProof);
    if (docLabel === "Financial/Income Documents")
      return isProposalDocumentSlotFilled(proposerDocs.incomeProof);
    if (docLabel === "Identification Proof") {
      return isProposalDocumentSlotFilled(
        proposerDocs.identificationProof || proposerDocs.idProof,
      );
    }
    if (docLabel === "Address Proof") {
      return (
        isProposalDocumentSlotFilled(proposerDocs.addressProof) ||
        isProposalDocumentSlotFilled(proposerDocs.addressProofPermanent) ||
        isProposalDocumentSlotFilled(proposerDocs.addressProofCommunication)
      );
    }
    return false;
  };

  const baseDocs = [
    "Photograph",
    "Address Proof",
    "Age Proof",
    "Financial/Income Documents",
    "Identification Proof",
  ];

  const pendingDocs = baseDocs.filter((doc) => !hasUploaded(doc));

  const addressProofOptions = useMemo(() => {
    return (apiCache.addressdocumentproof?.data || []).map((item) => ({
      label: item.documentName,
      value: item.documentID,
    }));
  }, [apiCache.addressdocumentproof]);

  const ageProofOptions = useMemo(() => {
    return (apiCache.agedocumentproof?.data || []).map((item) => ({
      label: item.documentName,
      value: item.documentID,
    }));
  }, [apiCache.agedocumentproof]);

  const identificationProofOptions = useMemo(() => {
    return (apiCache.identificationdocumentproof?.data || []).map((item) => ({
      label: item.documentName,
      value: item.documentID,
    }));
  }, [apiCache.identificationdocumentproof]);

  useEffect(() => {
    const addr = resolveSlotLaPayload("addressProof", {
      formData,
      apiCache,
      personType: "proposer",
    });
    const age = resolveSlotLaPayload("ageProof", {
      formData,
      apiCache,
      personType: "proposer",
    });
    const idp = resolveSlotLaPayload("identificationProof", {
      formData,
      apiCache,
      personType: "proposer",
    });

    const next = {};
    next.addressProofDocumentTypeCode = addr?.documentTypeCode || "";
    next.LA_AddressProofDocumentTypeCode = next.addressProofDocumentTypeCode;
    next.ageProofDocumentTypeCode = age?.documentTypeCode || "";
    next.LA_AgeProofDocumentTypeCode = next.ageProofDocumentTypeCode;
    next.identificationProofDocumentTypeCode = idp?.documentTypeCode || "";
    next.LA_IdentityProofDocumentTypeCode =
      next.identificationProofDocumentTypeCode;

    const changed =
      (formData.addressProofDocumentTypeCode || "") !==
      next.addressProofDocumentTypeCode ||
      (formData.LA_AddressProofDocumentTypeCode || "") !==
      next.LA_AddressProofDocumentTypeCode ||
      (formData.ageProofDocumentTypeCode || "") !==
      next.ageProofDocumentTypeCode ||
      (formData.LA_AgeProofDocumentTypeCode || "") !==
      next.LA_AgeProofDocumentTypeCode ||
      (formData.identificationProofDocumentTypeCode || "") !==
      next.identificationProofDocumentTypeCode ||
      (formData.LA_IdentityProofDocumentTypeCode || "") !==
      next.LA_IdentityProofDocumentTypeCode;

    if (changed) {
      onChange("__BULK__", next);
    }
  }, [
    formData.addressProof,
    formData.ageProof,
    formData.identificationProof,
    formData.addressProofDocumentTypeCode,
    formData.LA_AddressProofDocumentTypeCode,
    formData.ageProofDocumentTypeCode,
    formData.LA_AgeProofDocumentTypeCode,
    formData.identificationProofDocumentTypeCode,
    formData.LA_IdentityProofDocumentTypeCode,
    apiCache.addressdocumentproof,
    apiCache.agedocumentproof,
    apiCache.identificationdocumentproof,
  ]);
  const handleSameAddressChange = useCallback(
    (checked) => {
      onChange("__BULK__", {
        communicationAddressSameAsPermanent: checked,
        ...(checked && {
          communicationAddressLine1: "",
          communicationAddressLine2: "",
          communicationAddressLine3: "",
          communicationPinCode: "",
          communicationState: "",
          communicationCity: "",

          // ✅ Clear errors as well
          errors: {
            communicationAddressLine1: "",
            communicationAddressLine2: "",
            communicationAddressLine3: "",
            communicationPinCode: "",
            communicationState: "",
            communicationCity: "",
          },
        }),
      });
    },
    [onChange],
  );

  const handleTaxResidentialChange = useCallback(
    (checked) => {
      onChange("__BULK__", {
        taxResidentialOutsideIndia: checked,
        ...(checked && {
          residencyCountry: "",
          residencyTaxCountry: "",
          residencyTaxAddress: "",
          errors: {
            residencyCountry: "",
            residencyTaxCountry: "",
            residencyTaxAddress: "",
          },
        }),
      });
    },
    [onChange],
  );

  const findAadhaarOption = (options) => {
    return options.find(
      (opt) =>
        opt.label?.toLowerCase().includes("aadhaar") ||
        opt.label?.toLowerCase().includes("aadhar"),
    );
  };

  const maskIdentificationNumber = (value) => {
    if (!value) return "";
    const visibleDigits = 4;
    const maskedLength = value.length - visibleDigits;

    if (maskedLength <= 0) return value;

    return "*".repeat(maskedLength) + value.slice(-visibleDigits);
  };

  useEffect(() => {
    if (!isAadhaarVerified) return;

    const addressDoc = findAadhaarOption(addressProofOptions);
    const ageDoc = findAadhaarOption(ageProofOptions);
    const idDoc = findAadhaarOption(identificationProofOptions);

    const updatePayload = {};

    if (addressDoc) updatePayload.addressProof = addressDoc.value;
    if (ageDoc) {
      updatePayload.ageProof = ageDoc.value;
    }
    if (idDoc) updatePayload.identificationProof = idDoc.value;

    if (aadhaar?.number) {
      updatePayload.identificationNumber = aadhaar?.number;
    }

    if (Object.keys(updatePayload).length > 0) {
      onChange("__BULK__", updatePayload);
    }
  }, [
    isAadhaarVerified,
    addressProofOptions,
    ageProofOptions,
    identificationProofOptions,
  ]);

  const residentialStatusOptions = useMemo(() => {
    if (leadData?.nationality === "Yes") {
      return RESIDENTIAL_STATUS_OPTIONS.filter(
        (item) => item !== "Person of Indian Origin",
      );
    }

    return ["Person of Indian Origin"];
  }, [leadData?.nationality]);

  const incomeProofOptions = useMemo(
    () =>
      buildIdOptions(
        apiCache,
        "incomeproofs",
        "documentName",
        "documentID",
        "nishchitProduct",
      ),
    [apiCache.incomeproofs],
  );

  const salutationOptions = useMemo(() => {
    // "Other"/"Others" gender: every salutation is valid.
    const showAll = !leadGender || isGenderRestricted(leadGender);
    return (apiCache.salutationdetails?.data || [])
      .filter((item) => {
        const titleGender =
          SALUTATION_GENDER_MAP[item.salutationTitle] || "neutral";
        if (showAll) return true;
        if (titleGender === "neutral") return true;
        return titleGender === leadGender;
      })
      .map((item) => ({
        label: item.salutationTitle,
        value: item.salutationID,
      }));
  }, [apiCache.salutationdetails, leadGender]);

  const stateOptions = useMemo(() => {
    const uniqueStates = [...new Set(allPincodeData.map((item) => item.state))];

    return uniqueStates.map((state) => ({
      label: state,
      value: state,
    }));
  }, [allPincodeData]);

  const aadhaarLocation = useMemo(
    () => resolveAadhaarLocation(aadharAddress, allPincodeData),
    [aadharAddress, allPincodeData],
  );

  const countryOptions = useMemo(() => {
    return (apiCache.countrydetails?.data || []).map((item) => ({
      label: item.countryName,
      value: item.countryName,
    }));
  }, [apiCache.countrydetails]);

  // City API returns stateID (no stateName); filter by selected state's stateID
  const filteredCities = useMemo(() => {
    if (!formData.state) return [];

    const cities = [
      ...new Set(
        allPincodeData
          .filter((item) => item.state === formData.state)
          .map((item) => item.city),
      ),
    ];

    return cities.map((city) => ({
      label: city,
      value: city,
    }));
  }, [formData.state, allPincodeData]);

  const filteredCommunicationCities = useMemo(() => {
    if (!formData.communicationState) return [];

    const cities = [
      ...new Set(
        allPincodeData
          .filter((item) => item.state === formData.communicationState)
          .map((item) => item.city),
      ),
    ];

    return cities.map((city) => ({
      label: city,
      value: city,
    }));
  }, [formData.communicationState, allPincodeData]);

  const cleanNameInput = (value) => {
    return value
      .replace(/[^A-Za-z\s]/g, "")
      .replace(/\s+/g, " ")
      .trimStart()
      .slice(0, 30);
  };

  const handleStateChange = (name, value) => {
    onChange(name, value);

    onChange("city", "");
    onChange("pinCode", "");
  };

  const handleCommunicationStateChange = (name, value) => {
    onChange(name, value);

    onChange("communicationCity", "");
    onChange("communicationPinCode", "");
  };

  const handleCityChange = (name, value) => {
    onChange(name, value);

    const match = allPincodeData.find(
      (i) => i.state === formData.state && i.city === value,
    );

    if (match) {
      onChange("pinCode", String(match.pincode));
    }
  };

  const handleCommunicationCityChange = (name, value) => {
    onChange(name, value);

    const match = allPincodeData.find(
      (i) => i.state === formData.communicationState && i.city === value,
    );

    if (match) {
      onChange("communicationPinCode", String(match.pincode));
    }
  };
  const maritalStatusOptions = useMemo(
    () => buildIdOptions(apiCache, "maritalStatus"),
    [apiCache.maritalStatus],
  );
  const occupationOptions = useMemo(
    () => buildOccupationTypeOptions(apiCache, "Nishchit"),
    [apiCache.OccupationType],
  );

  useEffect(() => {
    const careOf = aadharData?.care_of || "";

    if (careOf.includes("S/O") || careOf.includes("D/O")) {
      const name = careOf.replace(/S\/O|D\/O/i, "").trim();
      onChange("fatherName", cleanPersonNamePreservingOverLimit(name));
    } else {
      onChange("fatherName", "");
    }
  }, [aadharData?.care_of]);

  useEffect(() => {
    if (!formData.pinCode || formData.pinCode.length !== 6) return;
    // Pincode master hasn't loaded yet — bail rather than treat "no data to
    // search" as "no match" and wipe a hydrated (already-saved) state/city.
    if (!allPincodeData.length) return;

    const timer = setTimeout(() => {
      const match = allPincodeData.find(
        (i) => String(i.pincode) === formData.pinCode,
      );

      if (match) {
        onChange("state", match.state);
        onChange("city", match.city);
        setIsLocationLocked(true);
      } else {
        onChange("state", "");
        onChange("city", "");
        setIsLocationLocked(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [formData.pinCode, allPincodeData]);

  useEffect(() => {
    if (
      !formData.communicationPinCode ||
      formData.communicationPinCode.length !== 6
    )
      return;
    // Pincode master hasn't loaded yet — bail rather than treat "no data to
    // search" as "no match" and wipe a hydrated (already-saved) state/city.
    if (!allPincodeData.length) return;

    const timer = setTimeout(() => {
      const match = allPincodeData.find(
        (i) => String(i.pincode) === formData.communicationPinCode,
      );

      if (match) {
        onChange("communicationState", match.state || "");
        onChange("communicationCity", match.city || "");
      } else {
        onChange("communicationState", "");
        onChange("communicationCity", "");
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [formData.communicationPinCode, allPincodeData]);

  return (
    <div className="proposal-section">
      {/* Personal Details */}
      <div className="proposal-form-grid">
        <h3 className="proposal-section__title">Personal Details</h3>
        <div className="form-wraper">
          <FormField label="Title" halfWidth error={errors?.title}>
            <SelectInput
              name="title"
              value={formData.title}
              onChange={onChange}
              options={salutationOptions}
              placeholder="Select"
            />
          </FormField>
          <FormField label="Full Name" halfWidth error={errors?.fullName}>
            <TextInput
              name="fullName"
              value={formData.fullName}
              onChange={(name, value) => {
                onChange(
                  name,
                  cleanPersonNamePreservingOverLimit(
                    value,
                    undefined,
                    formData.fullName,
                  ),
                );
              }}
              placeholder="Enter full name"
              disabled={isNameDisabled}
              maxLength={personNameInputMaxLength(formData.fullName)}
              autoComplete="off"
            />
          </FormField>
          <FormField label="Father's Name" halfWidth error={errors?.fatherName}>
            <TextInput
              name="fatherName"
              value={formData.fatherName}
              onChange={(name, value) => {
                onChange(
                  name,
                  isFatherFromAadhaar
                    ? cleanPersonNamePreservingOverLimit(
                      value,
                      undefined,
                      formData.fatherName,
                    )
                    : cleanNameInput(value).slice(0, 20),
                );
              }}
              maxLength={
                isFatherFromAadhaar
                  ? personNameInputMaxLength(formData.fatherName)
                  : 20
              }
              autoComplete="off"
              disabled={isFatherNameDisabled}
            />
          </FormField>
          <FormField label="Mother's Name" halfWidth error={errors?.motherName}>
            <TextInput
              name="motherName"
              value={formData.motherName}
              onChange={(name, value) => {
                onChange(name, cleanNameInput(value));
              }}
              maxLength={20}
              autoComplete="off"
            />
          </FormField>
          <FormField
            label="Marital Status"
            halfWidth
            error={errors?.maritalStatus}
          >
            <SelectInput
              name="maritalStatus"
              value={formData.maritalStatus}
              onChange={onChange}
              options={maritalStatusOptions}
              placeholder="Select"
            />
          </FormField>
          <FormField
            label="ABHA No."
            optional
            error={errors?.abhaNo}
            halfWidth
          >
            <TextInput
              name="abhaNo"
              value={formData.abhaNo}
              onChange={onChange}
              placeholder="e.g. 1234 5678 9012 34"
              maxLength={14}
              inputMode="numeric"
              autoComplete="off"
            />
          </FormField>

          <FormField label="Occupation" halfWidth error={errors?.occupation}>
            <SelectInput
              name="occupation"
              value={formData.occupation}
              onChange={onChange}
              options={occupationOptions}
              placeholder="Select"
            />
          </FormField>
          <FormField
            label="Job Description"
            halfWidth
            error={errors?.jobDiscription}
          >
            <TextInput
              name="jobDiscription"
              value={formData.jobDiscription}
              onChange={(name, value) => {
                if (/^[a-zA-Z0-9 ]*$/.test(value)) {
                  onChange(name, value);
                }
              }}
              maxLength={20}
              autoComplete="off"
            />
          </FormField>
        </div>
      </div>

      {/* Residential Details */}
      <div className="proposal-form-grid">
        <h4 className="proposal-section__subtitle">Residential Details</h4>
        <div className="form-wraper">
          <FormField
            label="Address Line 1"
            halfWidth
            error={errors?.addressLine1}
          >
            <TextInput
              name="addressLine1"
              autoComplete="address-line-one"
              value={formData.addressLine1}
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              // onChange={onChange}
              onChange={(name, value) => {
                if (/^[a-zA-Z0-9\s.,'#\-\/()&]{0,30}$/.test(value)) {
                  onChange(name, value);
                }
              }}
              disabled={
                !!aadharData?.address?.house || !!aadharData?.address?.street
              }
              maxLength={30}
            />
          </FormField>
          <FormField
            label="Address Line 2"
            halfWidth
            error={errors?.addressLine2}
          >
            <TextInput
              name="addressLine2"
              value={formData.addressLine2}
              autoComplete="address-line-two"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              // onChange={onChange}
              onChange={(name, value) => {
                if (/^[a-zA-Z0-9\s.,'#\-\/()&]{0,30}$/.test(value)) {
                  onChange(name, value);
                }
              }}
              disabled={!!aadharData?.address?.vtc}
              maxLength={30}
            />
          </FormField>
          <FormField label="Address Line 3" optional halfWidth>
            <TextInput
              name="addressLine3"
              value={formData.addressLine3}
              autoComplete="address-line-three"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              // onChange={onChange}
              onChange={(name, value) => {
                if (/^[a-zA-Z0-9\s.,'#\-\/()&]{0,30}$/.test(value)) {
                  onChange(name, value);
                }
              }}
              maxLength={30}
            />
          </FormField>
          <FormField label="Pin Code" halfWidth error={errors?.pinCode}>
            <TextInput
              name="pinCode"
              value={formData.pinCode}
              autoComplete="postal-code"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              onChange={onChange}
              maxLength={6}
              disabled={!!aadharData?.zip || !!leadData?.residencyPinCode}
            />
          </FormField>
          <FormField label="State" halfWidth error={errors?.state}>
            <SelectInput
              name="state"
              value={formData.state}
              onChange={handleStateChange}
              options={stateOptions}
              placeholder="Select"
              disabled={aadhaarLocation.hasLockedState || isLocationLocked}
            />
          </FormField>
          <FormField label="City" halfWidth error={errors?.city}>
            <SelectInput
              name="city"
              value={formData.city}
              onChange={handleCityChange}
              options={filteredCities}
              placeholder="Select"
              disabled={aadhaarLocation.hasLockedCity || isLocationLocked || !formData.state}
            />
          </FormField>
          <FormField label="Citizenship" halfWidth error={errors?.citizenship}>
            <SelectInput
              name="citizenship"
              value={aadharAddress?.country || formData?.citizenship || ""}
              onChange={onChange}
              options={countryOptions}
              disabled={!!aadharData?.address?.country}
            />
          </FormField>
          <FormField
            label="Residential Status"
            halfWidth
            error={errors?.residentialStatus}
          >
            <SelectInput
              name="residentialStatus"
              value={formData.residentialStatus}
              onChange={onChange}
              options={residentialStatusOptions}
            />
          </FormField>
          <div className="proposal-toggle-section bg-shadow-box">
            <div className="proposal-toggle-section-list">
              <ToggleSwitch
                label={TEXT_CONTENT.COM_ADDRESS}
                checked={sameAddress}
                error={errors?.communicationAddressSameAsPermanent}
                onChange={handleSameAddressChange}
                id="sameAddress"
              />
              {sameAddress === false && (
                <div className="form-wraper">
                  <FormField
                    label="Address Line 1"
                    halfWidth
                    error={errors?.communicationAddressLine1}
                  >
                    <TextInput
                      name="communicationAddressLine1"
                      value={formData.communicationAddressLine1}
                      // onChange={onChange}
                      onChange={(name, value) => {
                        if (/^[a-zA-Z0-9\s.,'#\-\/()&]{0,30}$/.test(value)) {
                          onChange(name, value);
                        }
                      }}
                      maxLength={30}
                      autoComplete="off"
                    />
                  </FormField>
                  <FormField
                    label="Address Line 2"
                    halfWidth
                    error={errors?.communicationAddressLine2}
                  >
                    <TextInput
                      name="communicationAddressLine2"
                      value={formData.communicationAddressLine2}
                      // onChange={onChange}
                      onChange={(name, value) => {
                        if (/^[a-zA-Z0-9\s.,'#\-\/()&]{0,30}$/.test(value)) {
                          onChange(name, value);
                        }
                      }}
                      maxLength={30}
                      autoComplete="off"
                    />
                  </FormField>
                  <FormField label="Address Line 3" optional halfWidth>
                    <TextInput
                      name="communicationAddressLine3"
                      value={formData.communicationAddressLine3}
                      // onChange={onChange}
                      onChange={(name, value) => {
                        if (/^[a-zA-Z0-9\s.,'#\-\/()&]{0,30}$/.test(value)) {
                          onChange(name, value);
                        }
                      }}
                      maxLength={30}
                      autoComplete="off"
                    />
                  </FormField>
                  <FormField
                    label="Pin Code"
                    halfWidth
                    error={errors?.communicationPinCode}
                  >
                    <TextInput
                      name="communicationPinCode"
                      value={formData.communicationPinCode}
                      onChange={onChange}
                      maxLength={6}
                      autoComplete="off"
                    />
                  </FormField>
                  <FormField
                    label="State"
                    halfWidth
                    error={errors?.communicationState}
                  >
                    <SelectInput
                      name="communicationState"
                      value={formData.communicationState}
                      onChange={handleCommunicationStateChange}
                      options={stateOptions}
                      placeholder="Select"
                    />
                  </FormField>
                  <FormField
                    label="City"
                    halfWidth
                    error={errors?.communicationCity}
                  >
                    <SelectInput
                      name="communicationCity"
                      value={formData.communicationCity}
                      onChange={handleCommunicationCityChange}
                      options={filteredCommunicationCities}
                      disabled={!formData.communicationState}
                    />
                  </FormField>
                </div>
              )}
            </div>
          </div>
          <div className="proposal-toggle-section bg-shadow-box">
            <div className="proposal-toggle-section-list">
              <ToggleSwitch
                label={TEXT_CONTENT.RES_ADDRESS}
                checked={taxResidentialOutsideIndia}
                error={errors?.taxResidentialOutsideIndia}
                onChange={handleTaxResidentialChange}
                id="taxResidentialOutsideIndia"
              />
              {taxResidentialOutsideIndia === true && (
                <div className="form-wraper">
                  <FormField
                    label="Country"
                    halfWidth
                    error={errors?.residencyCountry}
                  >
                    <SelectInput
                      name="residencyCountry"
                      value={formData.residencyCountry}
                      onChange={onChange}
                      options={countryOptions}
                      placeholder="Select"
                    />
                  </FormField>
                  <FormField
                    label="Country of Tax Residency"
                    halfWidth
                    error={errors?.residencyTaxCountry}
                  >
                    <SelectInput
                      name="residencyTaxCountry"
                      value={formData.residencyTaxCountry}
                      onChange={onChange}
                      options={countryOptions}
                    />
                  </FormField>
                  <FormField
                    label="Address of Tax Residency"
                    halfWidth
                    error={errors?.residencyTaxAddress}
                  >
                    <TextInput
                      name="residencyTaxAddress"
                      value={formData.residencyTaxAddress}
                      onChange={onChange}
                      maxLength={30}
                      autoComplete="off"
                    />
                  </FormField>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Choose Documents */}
      <div className="proposal-form-grid">
        <h4 className="proposal-section__subtitle">Choose Documents</h4>
        <p>{DOCUMENT_UPLOAD_TEXT}</p>
        <div className="form-wraper">
          <FormField label="Photograph" halfWidth error={errors?.photograph}>
            <SelectInput
              name="photograph"
              value={formData.photograph}
              onChange={onChange}
              options={PHOTOGRAPH_OPTIONS}
            />
          </FormField>

          <FormField
            label="Address Proof"
            halfWidth
            error={errors?.addressProof}
          >
            {/* Revert: replace WrappingSelect with SelectInput. */}
            <WrappingSelect
              name="addressProof"
              value={formData.addressProof}
              onChange={onChange}
              options={addressProofOptions}
              disabled={isAadhaarVerified}
            />
          </FormField>

          <FormField label="Age Proof" halfWidth error={errors?.ageProof}>
            <SelectInput
              name="ageProof"
              value={formData.ageProof}
              onChange={onChange}
              options={ageProofOptions}
              disabled={isAadhaarVerified}
            />
          </FormField>

          <FormField
            label="Financial/Income Proof"
            halfWidth
            error={errors?.incomeProof}
          >
            <SelectInput
              name="incomeProof"
              value={formData.incomeProof}
              onChange={onChange}
              options={incomeProofOptions}
            />
          </FormField>

          <FormField
            label="Identification Proof"
            halfWidth
            error={errors?.identificationProof}
          >
            <SelectInput
              name="identificationProof"
              value={formData.identificationProof}
              onChange={onChange}
              options={identificationProofOptions}
              disabled={isAadhaarVerified}
            />
          </FormField>

          <FormField
            label="Identification Number"
            halfWidth
            error={errors?.identificationNumber}
          >
            <TextInput
              name="identificationNumber"
              value={
                isFocused
                  ? formData.identificationNumber
                  : maskIdentificationNumber(formData.identificationNumber)
              }
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onChange={(name, value) => {
                const cleanValue = value.replace(/\*/g, "");
                onChange(name, cleanValue);
              }}
              disabled={isAadhaarVerified}
              autoComplete="off"
              maxLength={16}
            />
          </FormField>

          {/* Toggle questions */}
          <div className="proposal-toggle-section bg-shadow-box">
            <div className="proposal-toggle-section-list">
              <ToggleSwitch
                label={TEXT_CONTENT.TOGGLE_TWO}
                checked={formData.politicallyExposed}
                error={errors?.politicallyExposed}
                onChange={(value) => {
                  onChange("politicallyExposed", value);
                  if (!value) onChange("politicallyExposedDescription", "");
                }}
                id="politicallyExposed"
                infoNode={TEXT_CONTENT.TOLTIP_TEXT}
              />
              {formData.politicallyExposed === true && (
                <div className="proposal-resulting-fields">
                  <FormField
                    label="Description"
                    error={errors?.politicallyExposedDescription}
                  >
                    <TextInput
                      name="politicallyExposedDescription"
                      value={formData.politicallyExposedDescription}
                      onChange={onChange}
                      maxLength={50}
                    />
                  </FormField>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pending Documents Bar */}
      {pendingDocs.length > 0 && (
        <div className="proposal-pending-docs">
          <span className="proposal-pending-docs__icon">
            <svg
              width="11"
              height="13"
              viewBox="0 0 11 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.1754 3.41836L6.89414 0.13711C6.85058 0.0935934 6.79887 0.0590869 6.74196 0.0355612C6.68506 0.0120355 6.62408 -4.82746e-05 6.5625 1.44937e-07H0.9375C0.68886 1.44937e-07 0.450403 0.0987721 0.274587 0.274587C0.098772 0.450403 0 0.68886 0 0.9375V11.25C0 11.4986 0.098772 11.7371 0.274587 11.9129C0.450403 12.0887 0.68886 12.1875 0.9375 12.1875H9.375C9.62364 12.1875 9.8621 12.0887 10.0379 11.9129C10.2137 11.7371 10.3125 11.4986 10.3125 11.25V3.75C10.3125 3.68842 10.3005 3.62744 10.2769 3.57054C10.2534 3.51364 10.2189 3.46192 10.1754 3.41836ZM7.03125 1.6002L8.71231 3.28125H7.03125V1.6002ZM9.375 11.25H0.9375V0.9375H6.09375V3.75C6.09375 3.87432 6.14314 3.99355 6.23104 4.08146C6.31895 4.16936 6.43818 4.21875 6.5625 4.21875H9.375V11.25ZM7.5 6.5625C7.5 6.68682 7.45061 6.80605 7.36271 6.89396C7.2748 6.98186 7.15557 7.03125 7.03125 7.03125H3.28125C3.15693 7.03125 3.0377 6.98186 2.94979 6.89396C2.86189 6.80605 2.8125 6.68682 2.8125 6.5625C2.8125 6.43818 2.86189 6.31895 2.94979 6.23104C3.0377 6.14314 3.15693 6.09375 3.28125 6.09375H7.03125C7.15557 6.09375 7.2748 6.14314 7.36271 6.23104C7.45061 6.31895 7.5 6.43818 7.5 6.5625ZM7.5 8.4375C7.5 8.56182 7.45061 8.68105 7.36271 8.76896C7.2748 8.85686 7.15557 8.90625 7.03125 8.90625H3.28125C3.15693 8.90625 3.0377 8.85686 2.94979 8.76896C2.86189 8.68105 2.8125 8.56182 2.8125 8.4375C2.8125 8.31318 2.86189 8.19395 2.94979 8.10604C3.0377 8.01814 3.15693 7.96875 3.28125 7.96875H7.03125C7.15557 7.96875 7.2748 8.01814 7.36271 8.10604C7.45061 8.19395 7.5 8.31318 7.5 8.4375Z"
                fill="#DA1414"
              />
            </svg>
            Pending Documents:
          </span>
          {pendingDocs.map((doc, i) => (
            <React.Fragment key={doc}>
              {i > 0 && <span className="proposal-pending-docs__sep">|</span>}
              <span className="proposal-pending-docs__item">{doc}</span>
            </React.Fragment>
          ))}
          <button
            className="proposal-pending-docs__upload"
            onClick={onUploadClick}
            type="button"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.5 8.43794V12.9379C13.5 13.0871 13.4407 13.2302 13.3352 13.3357C13.2298 13.4412 13.0867 13.5004 12.9375 13.5004H0.5625C0.413316 13.5004 0.270242 13.4412 0.164753 13.3357C0.0592633 13.2302 0 13.0871 0 12.9379V8.43794C0 8.28876 0.0592633 8.14568 0.164753 8.04019C0.270242 7.9347 0.413316 7.87544 0.5625 7.87544C0.711684 7.87544 0.854758 7.9347 0.960247 8.04019C1.06574 8.14568 1.125 8.28876 1.125 8.43794V12.3754H12.375V8.43794C12.375 8.28876 12.4343 8.14568 12.5398 8.04019C12.6452 7.9347 12.7883 7.87544 12.9375 7.87544C13.0867 7.87544 13.2298 7.9347 13.3352 8.04019C13.4407 8.14568 13.5 8.28876 13.5 8.43794ZM4.33547 3.77341L6.1875 1.92068V8.43794C6.1875 8.58713 6.24676 8.7302 6.35225 8.83569C6.45774 8.94118 6.60082 9.00044 6.75 9.00044C6.89918 9.00044 7.04226 8.94118 7.14775 8.83569C7.25324 8.7302 7.3125 8.58713 7.3125 8.43794V1.92068L9.16453 3.77341C9.27008 3.87896 9.41323 3.93825 9.5625 3.93825C9.71177 3.93825 9.85492 3.87896 9.96047 3.77341C10.066 3.66786 10.1253 3.52471 10.1253 3.37544C10.1253 3.22618 10.066 3.08302 9.96047 2.97747L7.14797 0.164974C7.09573 0.112674 7.03369 0.0711849 6.9654 0.0428775C6.89712 0.0145701 6.82392 0 6.75 0C6.67608 0 6.60288 0.0145701 6.5346 0.0428775C6.46631 0.0711849 6.40427 0.112674 6.35203 0.164974L3.53953 2.97747C3.43398 3.08302 3.37469 3.22618 3.37469 3.37544C3.37469 3.52471 3.43398 3.66786 3.53953 3.77341C3.64508 3.87896 3.78823 3.93825 3.9375 3.93825C4.08677 3.93825 4.22992 3.87896 4.33547 3.77341Z"
                fill="#97262A"
              />
            </svg>
            <span>Upload Now</span>
          </button>
        </div>
      )}

      <div className="proposal-form-actions">
        <button
          className="proposal-btn-continue proposal-btn-continue btn primary-button regular"
          onClick={onContinue}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   JOB DETAILS SECTION
   ═══════════════════════════════════════════════════ */

const JobDetails = ({ formData, errors, onChange }) => {
  const apiCache = useSelector((state) => state.journey.apiCache || {});
  const occupationOptions = useMemo(
    () => buildOccupationTypeOptions(apiCache, "Nishchit"),
    [apiCache.OccupationType],
  );

  return (
    <div className="proposal-section">
      <div className="proposal-form-grid">
        <h3 className="proposal-section__title">Job Details</h3>
        <div className="form-wraper">
          <FormField label="Occupation" halfWidth error={errors?.occupation}>
            <SelectInput
              name="occupation"
              value={formData.occupation}
              onChange={onChange}
              options={occupationOptions}
              placeholder="Select"
            />
          </FormField>
          <FormField
            label="Name of Employer"
            halfWidth
            error={errors?.employerName}
          >
            <TextInput
              name="employerName"
              value={formData.employerName}
              onChange={onChange}
              maxLength={30}
            />
          </FormField>
          <FormField
            label="Annual Income"
            halfWidth
            error={errors?.annualIncome}
          >
            <TextInput
              name="annualIncome"
              value={formData.annualIncome}
              onChange={onChange}
            />
          </FormField>
          <FormField
            label="Job Description"
            halfWidth
            error={errors?.jobDescription}
          >
            <TextInput
              name="ifscCode"
              value={formData.jobDescription}
              maxLength={20}
              onChange={onChange}
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   BANK DETAILS SECTION
   ═══════════════════════════════════════════════════ */
const BankDetails = ({
  formData,
  errors,
  onChange,
  onContinue,
  setLoading,
  onUploadClick,
  documents = {},
  onBankProofUploaded,
}) => {
  const dispatch = useDispatch();
  const apiCache = useSelector((state) => state.journey.apiCache || {});
  const leadData = useSelector((state) => state.journey.leadFormData || {});
  const applicationNumberFromStore = useSelector(
    (state) => state.journey?.applicationNumber,
  );
  const applicationNumber =
    applicationNumberFromStore || leadData?.applicationNumber || "";
  const [showNachModal, setShowNachModal] = useState(false);
  const [bankUploadError, setBankUploadError] = useState("");
  const selectedBankProofTypeRef = useRef(formData.bankAccountProof || "");
  const [showUploadDropdown, setShowUploadDropdown] = useState(false);
  const [bankSearch, setBankSearch] = useState(formData.bankName || "");
  const [filteredBanks, setFilteredBanks] = useState([]);
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const paymentFrequency = useSelector(
    (state) => state.journey?.quoteResult?.premiumPaymentFrequency,
  );
  const bankFileInputRef = useRef(null);
  const bankFileName = formData.file?.name || "";

  const isVerifyEnabled =
    [
      formData.bankName,
      formData.branchName,
      formData.accountNumber,
      formData.ifscCode,
    ].every((value) => (value || "").trim() !== "") &&
    !(
      (paymentFrequency === "Monthly" || paymentFrequency === "Quarterly") &&
      !formData.nachAccepted
    );

  const bankOptions = useMemo(() => {
    return (apiCache.bankdetails?.data || []).map((item) => ({
      label: item.bankName,
      value: item.bankCode,
    }));
  }, [apiCache.bankdetails]);

  useEffect(() => {
    if (!bankSearch.trim()) {
      setFilteredBanks([]);
      return;
    }

    const filtered = bankOptions.filter((bank) =>
      bank.label.toLowerCase().includes(bankSearch.toLowerCase()),
    );

    setFilteredBanks(filtered);
  }, [bankSearch, bankOptions]);

  const handleBankFieldChange = useCallback(
    (name, value) => {
      if (name === "accountNumber") {
        value = String(value || "").replace(/\D/g, "").slice(0, 18);
      }
      if (name === "bankName") {
        value = String(value || "").slice(0, 30);
      }
      if (name === "branchName") {
        value = value.replace(/^\s+/, "").slice(0, 30);
      }
      onChange(name, value);
      if (formData.bankVerified) {
        onChange("bankVerified", false);
      }
    },
    [onChange, formData.bankVerified],
  );

  // The inline bank upload used to only keep the raw File on the bank stage,
  // so `documents.proposer.bankAccountProof` stayed empty and the Proposal
  // Review screen showed the Bank Account Proof slot as blank. Upload it the
  // same way UploadDocumentsModal does and store the slot meta in `documents`.
  const handleBankFileChange = useCallback(
    async (event) => {
      const selectedFile = event.target.files?.[0];
      event.target.value = "";
      if (!selectedFile) return;

      const ext = (selectedFile.name.split(".").pop() || "").toLowerCase();
      if (!["jpg", "jpeg", "png", "pdf"].includes(ext)) {
        setBankUploadError("Invalid format. Upload jpg, png, or pdf.");
        return;
      }
      const sizeError = getDocumentUploadOverSizeError(selectedFile);
      if (sizeError) {
        setBankUploadError(sizeError);
        return;
      }
      if (!applicationNumber) {
        setBankUploadError(
          "Application number is missing. Please restart the journey.",
        );
        return;
      }

      setBankUploadError("");
      setLoading?.(true);
      try {
        const proofType =
          selectedBankProofTypeRef.current || formData.bankAccountProof;
        const la =
          resolveSlotLaPayload("bankAccountProof", {
            formData: { ...formData, bankAccountProof: proofType },
            apiCache,
            personType: "proposer",
          }) || FALLBACK_PR_BY_SLOT.bankAccountProof;

        const uploadData = new FormData();
        uploadData.append("applicationNumber", applicationNumber);
        const ownershipDob =
          leadData?.dateOfBirth ||
          formData.dateOfBirth ||
          formData.dob ||
          formData.liDob ||
          "";
        if (ownershipDob) {
          uploadData.append("dateOfBirth", ownershipDob);
        }
        uploadData.append("personType", "proposer");
        uploadData.append("documentCategory", "KYC");
        uploadData.append("documentTypeCode", la.documentTypeCode);
        uploadData.append("documentType", la.documentType);
        uploadData.append("docFormat", ext.toUpperCase());
        uploadData.append("docPageSideFB", "M");
        uploadData.append("addedBy", "system");
        uploadData.append("sourceFrom", "online");
        uploadData.append("file", selectedFile);

        const response = await dispatch(uploadDocumentAction(uploadData));
        if (!response?.success) {
          throw new Error(response?.errorMessage || "Upload failed");
        }
        const slotMeta = await serializeDocumentFileMetaWithImagePreview(
          selectedFile,
          response,
        );
        onChange("file", selectedFile);
        onChange("bankAccountProofDocumentTypeCode", la.documentTypeCode);
        onBankProofUploaded?.(slotMeta);
      } catch (error) {
        logError("Bank document upload failed", error);
        setBankUploadError(
          error?.message || "Document upload failed. Please try again.",
        );
      } finally {
        setLoading?.(false);
      }
    },
    [
      applicationNumber,
      apiCache,
      dispatch,
      formData,
      leadData,
      onBankProofUploaded,
      onChange,
      setLoading,
    ],
  );

  const handleVerify = useCallback(async () => {
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!isVerifyEnabled) return;
    if (!formData.accountNumber || formData.accountNumber.length < 9) {
      setErrorMsg("Account Number must be at least 9 characters.");
      setShowError(true);
      onChange("bankVerified", false);
      return;
    } else if (!ifscRegex.test(formData.ifscCode?.trim().toUpperCase())) {
      setErrorMsg("Please enter valid IFSC code.");
      setShowError(true);
      onChange("bankVerified", false);
      return;
    }
    else {
      setErrorMsg("");
    }
    setLoading(true);
    const initIso = new Date().toISOString();
    onChange("PennyDropServiceInit_date", initIso);
    try {
      const payload = {
        beneAccNo: formData.accountNumber,
        beneIFSC: formData.ifscCode,
        custName: formData.fullName || formData.bankName,
        mobile: leadData?.mobileNumber,
        source: "InfoAxon",
      };
      onChange("pennyDropRequest", JSON.stringify(payload));
      const response = await validateBankAccountAction(payload)();
      const recvIso = new Date().toISOString();
      onChange("PennyDropResponseRecv_date", recvIso);
      if (response != null) {
        onChange("pennyDropResponse", JSON.stringify(response));
      } else {
        onChange("pennyDropResponse", "");
      }
      if (response?.success === true || response?.status === "success") {
        onChange("bankVerified", true);
        onChange("pennyDropStatus", "Passed");
        onChange("pennyDropRemark", "Bank a/c is verified via penny drop");
        if (response?.BeneName) {
          onChange("Name_as_PerBank", response.BeneName);
        }
      } else {
        onChange("bankVerified", false);
        onChange("pennyDropStatus", "Failed");
        if (response?.errormessage) {
          onChange("pennyDropRemark", response.errormessage);
          setErrorMsg(response.errormessage);
          setShowError(true);
        } else {
          onChange("pennyDropRemark", "Penny drop failed");
        }
      }
      if (response?.errorcode != null && response?.errorcode !== "") {
        onChange("pennyDropErrorCode", String(response.errorcode));
      }
    } catch (error) {
      logError("Bank verification failed", error);
      onChange("PennyDropResponseRecv_date", new Date().toISOString());
      onChange("pennyDropStatus", "Failed");
      onChange(
        "pennyDropRemark",
        error?.message || "Penny drop request failed",
      );
      onChange("bankVerified", false);
    } finally {
      setLoading(false);
    }
  }, [isVerifyEnabled, formData, leadData, onChange]);

  const hasUploaded = (docLabel) => {
    const proposerDocs = documents?.proposer || {};
    if (docLabel === "Photograph")
      return isProposalDocumentSlotFilled(proposerDocs.photograph);
    if (docLabel === "Age Proof")
      return isProposalDocumentSlotFilled(proposerDocs.ageProof);
    if (docLabel === "Financial/Income Documents")
      return isProposalDocumentSlotFilled(proposerDocs.incomeProof);
    if (docLabel === "Identification Proof") {
      return isProposalDocumentSlotFilled(
        proposerDocs.identificationProof || proposerDocs.idProof,
      );
    }
    if (docLabel === "Address Proof") {
      return (
        isProposalDocumentSlotFilled(proposerDocs.addressProof) ||
        isProposalDocumentSlotFilled(proposerDocs.addressProofPermanent) ||
        isProposalDocumentSlotFilled(proposerDocs.addressProofCommunication)
      );
    }
    return false;
  };
  const baseDocs = [
    "Photograph",
    "Address Proof",
    "Age Proof",
    "Financial/Income Documents",
    "Identification Proof",
  ];

  const pendingDocs = baseDocs.filter((doc) => !hasUploaded(doc));

  return (
    <div className="proposal-section">
      <div className="proposal-form-grid overflow-hidden-grid">
        <h3 className="proposal-section__title">Bank Details</h3>
        {/* Auto-fetch banner */}
        <div className="proposal-autofetch-banner">
          <div className="proposal-autofetch-banner__left">
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="40" height="40" rx="6" fill="#FACED0" />
              <g clip-path="url(#clip0_33893_5377)">
                <path
                  d="M28.2777 19.1333C28.2493 19.0131 28.1917 18.9017 28.1099 18.8092C28.0281 18.7166 27.9247 18.6457 27.809 18.6026L22.408 16.5767L23.7824 9.70202C23.8135 9.54229 23.7918 9.37678 23.7206 9.23044C23.6494 9.08411 23.5326 8.96491 23.3877 8.89083C23.2428 8.81674 23.0777 8.79179 22.9174 8.81974C22.7571 8.84769 22.6102 8.92703 22.499 9.04577L11.999 20.2958C11.9137 20.3856 11.852 20.4952 11.8194 20.6147C11.7868 20.7343 11.7843 20.86 11.8122 20.9807C11.84 21.1014 11.8974 21.2133 11.979 21.3065C12.0607 21.3997 12.1642 21.4711 12.2802 21.5145L17.683 23.5405L16.3124 30.4076C16.2813 30.5674 16.3029 30.7329 16.3741 30.8792C16.4454 31.0256 16.5622 31.1448 16.7071 31.2188C16.852 31.2929 17.0171 31.3179 17.1774 31.2899C17.3377 31.262 17.4846 31.1826 17.5958 31.0639L28.0958 19.8139C28.1796 19.724 28.2399 19.6149 28.2717 19.4963C28.3034 19.3776 28.3054 19.2529 28.2777 19.1333ZM18.3008 28.1173L19.2824 23.2067C19.3175 23.0326 19.2897 22.8516 19.2038 22.696C19.118 22.5405 18.9797 22.4205 18.8136 22.3573L13.8599 20.4964L21.793 11.997L20.8124 16.9076C20.7773 17.0818 20.8051 17.2628 20.8909 17.4183C20.9768 17.5739 21.1151 17.6939 21.2811 17.757L26.2311 19.6133L18.3008 28.1173Z"
                  fill="#97262A"
                />
              </g>
              <defs>
                <clipPath id="clip0_33893_5377">
                  <rect
                    width="24"
                    height="24"
                    fill="white"
                    transform="translate(8.04688 8.05469)"
                  />
                </clipPath>
              </defs>
            </svg>
            <div className="clip-fetch-section">
              <strong>
                Verify your bank details for a hassle free claim experience
              </strong>
            </div>

            <div className="proposal-upload-wrapper">
              <button
                className="proposal-btn-upload btn primary-button regular"
                type="button"
                onClick={() => setShowUploadDropdown((prev) => !prev)}
              >
                Upload
                <svg
                  width="9"
                  height="5"
                  viewBox="0 0 9 5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.599609 0.601562L4.09961 4.10156L7.59961 0.601562"
                    stroke="white"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>

              {showUploadDropdown && (
                <div className="proposal-upload-dropdown">
                  {["Cancelled Cheque", "Passbook", "Bank Statement"].map(
                    (type) => (
                      <div
                        key={type}
                        className="proposal-upload-option"
                        onClick={() => {
                          onChange("bankAccountProof", type);
                          selectedBankProofTypeRef.current = type;
                          setShowUploadDropdown(false);
                          bankFileInputRef.current?.click();
                        }}
                      >
                        {type}
                      </div>
                    ),
                  )}
                </div>
              )}

              <input
                ref={bankFileInputRef}
                type="file"
                className="proposal-bank-file-input"
                style={{ display: "none" }}
                onChange={handleBankFileChange}
                autoComplete="off"
              />
            </div>

            {bankFileName && (
              <div className="proposal-uploaded-file-name">
                Selected file: {bankFileName}
              </div>
            )}
            {bankUploadError && (
              <div className="pivc-error" role="alert">
                {bankUploadError}
              </div>
            )}
          </div>
        </div>
        <div className="form-wraper">
          <FormField label="Name of Bank" halfWidth error={errors?.bankName}>
            <div className="proposal-autocomplete-wrapper">
              <input
                type="text"
                className="proposal-input"
                value={bankSearch}
                placeholder="Type bank name"
                maxLength={30}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 30);
                  setBankSearch(value);
                  setShowBankDropdown(true);
                  handleBankFieldChange("bankName", value);
                }}
                onFocus={() => setShowBankDropdown(true)}
                onBlur={() => setShowBankDropdown(false)}
                autoComplete="off"
              />
              {showBankDropdown && filteredBanks.length > 0 && (
                <div className="proposal-autocomplete-dropdown">
                  {filteredBanks.slice(0, 10).map((bank) => (
                    <div
                      key={bank.value}
                      className="proposal-autocomplete-option"
                      onMouseDown={() => {
                        const limited = String(bank.label || "").slice(0, 30);
                        setBankSearch(limited);
                        handleBankFieldChange("bankName", limited);
                        setShowBankDropdown(false);
                      }}
                    >
                      {bank.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </FormField>
          <FormField label="Branch Name" halfWidth error={errors?.branchName}>
            <TextInput
              name="branchName"
              value={formData.branchName}
              onChange={(name, value) => {
                const branchValue = value.trimStart();
                if (/^[A-Za-z0-9 .,&'()-]{0,30}$/.test(branchValue)) {
                  handleBankFieldChange(name, branchValue);
                }
              }}
              maxLength={30}
            />
          </FormField>
          <FormField
            label="Account Number"
            halfWidth
            error={errors?.accountNumber}
          >
            <TextInput
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleBankFieldChange}
              maxLength={18}
            />
          </FormField>
          <FormField label="IFSC Code" halfWidth error={errors?.ifscCode}>
            <TextInput
              name="ifscCode"
              value={formData.ifscCode}
              maxLength={11}
              onChange={(name, value) => {
                const upperValue = value.toUpperCase().trim();
                if (/^[A-Z0-9]{0,11}$/.test(upperValue)) {
                  handleBankFieldChange(name, upperValue);
                }
              }}
              placeholder="e.g. HDFC0001234"
            />
          </FormField>

          {/* Verify banner */}
          <div className="proposal-verify-banner">
            <div className="proposal-verify-banner__left">
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="40" height="40" rx="6" fill="#FCE4E5" />
                <path
                  d="M9.66667 18.333H12.1667V24.9999H10.5C10.279 24.9999 10.067 25.0877 9.91074 25.244C9.75446 25.4002 9.66667 25.6122 9.66667 25.8332C9.66667 26.0542 9.75446 26.2662 9.91074 26.4225C10.067 26.5788 10.279 26.6666 10.5 26.6666H30.5C30.721 26.6666 30.933 26.5788 31.0893 26.4225C31.2455 26.2662 31.3333 26.0542 31.3333 25.8332C31.3333 25.6122 31.2455 25.4002 31.0893 25.244C30.933 25.0877 30.721 24.9999 30.5 24.9999H28.8333V18.333H31.3333C31.5147 18.3328 31.691 18.2735 31.8355 18.1641C31.9801 18.0546 32.085 17.901 32.1344 17.7265C32.1837 17.5521 32.1748 17.3663 32.109 17.1973C32.0432 17.0283 31.9241 16.8854 31.7698 16.7903L20.9365 10.1234C20.8052 10.0427 20.6541 10 20.5 10C20.3459 10 20.1948 10.0427 20.0635 10.1234L9.23021 16.7903C9.07586 16.8854 8.95676 17.0283 8.89097 17.1973C8.82518 17.3663 8.81629 17.5521 8.86565 17.7265C8.91501 17.901 9.01992 18.0546 9.16448 18.1641C9.30904 18.2735 9.48535 18.3328 9.66667 18.333ZM13.8333 18.333H17.1667V24.9999H13.8333V18.333ZM22.1667 18.333V24.9999H18.8333V18.333H22.1667ZM27.1667 24.9999H23.8333V18.333H27.1667V24.9999ZM20.5 11.811L28.3896 16.6663H12.6104L20.5 11.811ZM33 29.1666C33 29.3877 32.9122 29.5996 32.7559 29.7559C32.5996 29.9122 32.3877 30 32.1667 30H8.83333C8.61232 30 8.40036 29.9122 8.24408 29.7559C8.0878 29.5996 8 29.3877 8 29.1666C8 28.9456 8.0878 28.7337 8.24408 28.5774C8.40036 28.4211 8.61232 28.3333 8.83333 28.3333H32.1667C32.3877 28.3333 32.5996 28.4211 32.7559 28.5774C32.9122 28.7337 33 28.9456 33 29.1666Z"
                  fill="#97262A"
                />
              </svg>

              <div className="clip-fetch-section">
                <p>
                  ₹1 will be credited to confirm your bank account ownership
                </p>
              </div>
            </div>
            <button
              className={`proposal-btn-verify btn primary-button regular  ${formData.bankVerified ? "verified" : ""}`}
              type="button"
              disabled={!isVerifyEnabled}
              onClick={handleVerify}
            >
              {formData.bankVerified ? "Verified ✓" : "Verify"}
            </button>
          </div>
        </div>

        <section className="quote-submit-section">
          {showError && (
            <div
              className="error-message error-message-inline"
              role="alert"
              aria-live="assertive"
            >
              {errorMsg}
            </div>
          )}

          {/* Pending Documents Bar */}
          {pendingDocs.length > 0 && (
            <div className="proposal-pending-docs proposal-pending-docs--in-footer">
              <span className="proposal-pending-docs__icon">
                <svg
                  width="11"
                  height="13"
                  viewBox="0 0 11 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.1754 3.41836L6.89414 0.13711C6.85058 0.0935934 6.79887 0.0590869 6.74196 0.0355612C6.68506 0.0120355 6.62408 -4.82746e-05 6.5625 1.44937e-07H0.9375C0.68886 1.44937e-07 0.450403 0.0987721 0.274587 0.274587C0.098772 0.450403 0 0.68886 0 0.9375V11.25C0 11.4986 0.098772 11.7371 0.274587 11.9129C0.450403 12.0887 0.68886 12.1875 0.9375 12.1875H9.375C9.62364 12.1875 9.8621 12.0887 10.0379 11.9129C10.2137 11.7371 10.3125 11.4986 10.3125 11.25V3.75C10.3125 3.68842 10.3005 3.62744 10.2769 3.57054C10.2534 3.51364 10.2189 3.46192 10.1754 3.41836ZM7.03125 1.6002L8.71231 3.28125H7.03125V1.6002ZM9.375 11.25H0.9375V0.9375H6.09375V3.75C6.09375 3.87432 6.14314 3.99355 6.23104 4.08146C6.31895 4.16936 6.43818 4.21875 6.5625 4.21875H9.375V11.25ZM7.5 6.5625C7.5 6.68682 7.45061 6.80605 7.36271 6.89396C7.2748 6.98186 7.15557 7.03125 7.03125 7.03125H3.28125C3.15693 7.03125 3.0377 6.98186 2.94979 6.89396C2.86189 6.80605 2.8125 6.68682 2.8125 6.5625C2.8125 6.43818 2.86189 6.31895 2.94979 6.23104C3.0377 6.14314 3.15693 6.09375 3.28125 6.09375H7.03125C7.15557 6.09375 7.2748 6.14314 7.36271 6.23104C7.45061 6.31895 7.5 6.43818 7.5 6.5625ZM7.5 8.4375C7.5 8.56182 7.45061 8.68105 7.36271 8.76896C7.2748 8.85686 7.15557 8.90625 7.03125 8.90625H3.28125C3.15693 8.90625 3.0377 8.85686 2.94979 8.76896C2.86189 8.68105 2.8125 8.56182 2.8125 8.4375C2.8125 8.31318 2.86189 8.19395 2.94979 8.10604C3.0377 8.01814 3.15693 7.96875 3.28125 7.96875H7.03125C7.15557 7.96875 7.2748 8.01814 7.36271 8.10604C7.45061 8.19395 7.5 8.31318 7.5 8.4375Z"
                    fill="#DA1414"
                  />
                </svg>
                Pending Documents:
              </span>
              {pendingDocs.map((doc, i) => (
                <React.Fragment key={doc}>
                  {i > 0 && <span className="proposal-pending-docs__sep">|</span>}
                  <span className="proposal-pending-docs__item">{doc}</span>
                </React.Fragment>
              ))}
              <button
                className="proposal-pending-docs__upload"
                onClick={onUploadClick}
                type="button"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.5 8.43794V12.9379C13.5 13.0871 13.4407 13.2302 13.3352 13.3357C13.2298 13.4412 13.0867 13.5004 12.9375 13.5004H0.5625C0.413316 13.5004 0.270242 13.4412 0.164753 13.3357C0.0592633 13.2302 0 13.0871 0 12.9379V8.43794C0 8.28876 0.0592633 8.14568 0.164753 8.04019C0.270242 7.9347 0.413316 7.87544 0.5625 7.87544C0.711684 7.87544 0.854758 7.9347 0.960247 8.04019C1.06574 8.14568 1.125 8.28876 1.125 8.43794V12.3754H12.375V8.43794C12.375 8.28876 12.4343 8.14568 12.5398 8.04019C12.6452 7.9347 12.7883 7.87544 12.9375 7.87544C13.0867 7.87544 13.2298 7.9347 13.3352 8.04019C13.4407 8.14568 13.5 8.28876 13.5 8.43794ZM4.33547 3.77341L6.1875 1.92068V8.43794C6.1875 8.58713 6.24676 8.7302 6.35225 8.83569C6.45774 8.94118 6.60082 9.00044 6.75 9.00044C6.89918 9.00044 7.04226 8.94118 7.14775 8.83569C7.25324 8.7302 7.3125 8.58713 7.3125 8.43794V1.92068L9.16453 3.77341C9.27008 3.87896 9.41323 3.93825 9.5625 3.93825C9.71177 3.93825 9.85492 3.87896 9.96047 3.77341C10.066 3.66786 10.1253 3.52471 10.1253 3.37544C10.1253 3.22618 10.066 3.08302 9.96047 2.97747L7.14797 0.164974C7.09573 0.112674 7.03369 0.0711849 6.9654 0.0428775C6.89712 0.0145701 6.82392 0 6.75 0C6.67608 0 6.60288 0.0145701 6.5346 0.0428775C6.46631 0.0711849 6.40427 0.112674 6.35203 0.164974L3.53953 2.97747C3.43398 3.08302 3.37469 3.22618 3.37469 3.37544C3.37469 3.52471 3.43398 3.66786 3.53953 3.77341C3.64508 3.87896 3.78823 3.93825 3.9375 3.93825C4.08677 3.93825 4.22992 3.87896 4.33547 3.77341Z"
                    fill="#97262A"
                  />
                </svg>
                <span>Upload Now</span>
              </button>
            </div>
          )}

          <div className="quote-submit-strip">
            <button
              className="btn-submit-quote btn primary-button regular ml-auto"
              onClick={onContinue}
              disabled={!formData.bankVerified}
            >
              Continue
            </button>
          </div>
        </section>

        {/* <div className="proposal-form-actions">
          <button
            className="proposal-btn-continue btn primary-button regular"
            onClick={onContinue}
            disabled={!formData.bankVerified}
          >
            Continue
          </button>
        </div> */}

        {showNachModal && (
          <div
            className="proposal-info-modal-backdrop"
            onClick={() => setShowNachModal(false)}
          >
            <div
              className="proposal-info-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="proposal-info-modal__header">
                <h4>NACH Mandate</h4>
                <button
                  type="button"
                  onClick={() => setShowNachModal(false)}
                  aria-label="Close NACH mandate modal"
                >
                  ×
                </button>
              </div>
              <div className="proposal-info-modal__body">
                <p>
                  The National Payments Corporation of India (NPCI) offers to
                  banks, financial institutions, Corporates and Government/s a
                  service termed as &quot;National Automated Clearing House
                  (NACH)&quot; which includes both Debit and Credit. It shall be
                  referred to as NACH. NACH (Debit) &amp; NACH (Credit) aims at
                  facilitating interbank high volume, low value debit/credit
                  transactions, which are repetitive in nature, electronically
                  using the NPCI service.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   LIFE ASSURED TAB (MAIN)
   ═══════════════════════════════════════════════════ */
const INITIAL_FORM_DATA = {
  title: "",
  fullName: "",
  fatherName: "",
  motherName: "",
  maritalStatus: "",
  // panNumber: "",
  abhaNo: "",
  aadhar: "",
  ckcyNo: "",
  education: "",
  residenceOfProposer: "",
  residentStatus: "",
  weight: "",
  height: "",
  nationality: "",
  occupation: "",
  annualIncome: "",
  employerName: "",
  // occupationCategory: "",
  jobDiscription: "",
  addressLine1: "",
  addressLine2: "",
  addressLine3: "",
  pinCode: "",
  state: "",
  city: "",
  citizenship: "",
  residentialStatus: "Resident Individual",
  communicationAddressSameAsPermanent: null,
  taxResidentialOutsideIndia: null,
  communicationAddressLine1: "",
  communicationAddressLine2: "",
  communicationAddressLine3: "",
  communicationCity: "",
  communicationState: "",
  communicationPinCode: "",
  residencyCountry: "",
  residencyTaxCountry: "",
  residencyTaxAddress: "",
  bankName: "",
  branchName: "",
  accountNumber: "",
  ifscCode: "",
  bankAccountProof: "",
  file: null,
  nachAccepted: true,
  bankVerified: false,
  photograph: "",
  addressProof: "",
  ageProof: "",
  incomeProof: "",
  // surrogateIncomeProof: "",
  identificationProof: "",
  identificationNumber: "",
  organisation: "",
  jobDescription: "",
  criminalCharges: false,
  politicallyExposed: null,
  existingPolicies: false,
  otherApplication: false,
  criminalChargesDescription: "",
  politicallyExposedDescription: "",
  existingPolicyCompany: "",
  existingPolicySumAssured: "",
  existingPolicyStatus: "",
  otherApplicationCompany: "",
  otherApplicationSumAssured: "",
  otherApplicationReason: "",
};

// Payload optimization: each side-step only persists the fields it owns,
// instead of the whole shared formData object (matches tulip pattern).
const BANK_STEP_KEYS = [
  "bankName",
  "branchName",
  "accountNumber",
  "ifscCode",
  "bankAccountProof",
  "file",
  "nachAccepted",
  "bankVerified",
  "PennyDropServiceInit_date",
  "PennyDropResponseRecv_date",
  "pennyDropRequest",
  "pennyDropResponse",
  "pennyDropStatus",
  "pennyDropRemark",
  "pennyDropErrorCode",
  "Name_as_PerBank",
];

const pickKeys = (source, keys) =>
  keys.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      acc[key] = source[key];
    }
    return acc;
  }, {});

// Adds each dropdown's display Label alongside its existing stored value —
// and, for apiCache/master-backed dropdowns, the master's own BizCode too —
// under the master's own field name (e.g. `salutationBizCode`). The
// dropdown form values themselves are unchanged; this only augments the
// outgoing payload. Mirrors inlic-tulip-journey/ProposerTab.jsx.
const withStepBizCodes = (payload, formData, stepId, apiCache) => {
  if (stepId === "bank") {
    return {
      ...payload,
      bankNameLabel: formData.bankName || "",
      bankBizCode: resolveBizCode(apiCache, "bankByName", formData.bankName),
    };
  }

  const salutation = resolveLabelAndBizCode(
    apiCache,
    "salutation",
    formData.title,
  );
  const address = resolveLabelAndBizCode(
    apiCache,
    "addressProofById",
    formData.addressProof,
  );
  const age = resolveLabelAndBizCode(
    apiCache,
    "ageProofById",
    formData.ageProof,
  );
  const identification = resolveLabelAndBizCode(
    apiCache,
    "identificationProofById",
    formData.identificationProof,
  );
  const incomeProof = resolveLabelAndBizCode(
    apiCache,
    "incomeProofById",
    formData.incomeProof,
  );

  const maritalStatus = resolveLabelAndBizCode(
    apiCache,
    "maritalStatusById",
    formData.maritalStatus,
  );
  const occupationType = resolveLabelAndBizCode(
    apiCache,
    "occupationTypeById",
    formData.occupation,
  );

  return {
    ...payload,
    titleLabel: salutation.label,
    salutationBizCode: salutation.bizCode,
    // maritalStatus, residentialStatus and photograph are static option
    // lists (not apiCache/master-backed) in this journey, so their stored
    // value already *is* the display label — no BizCode exists for them.
    // incomeProof is master-backed (incomeproofs / documentID).
    // maritalStatusLabel: resolveStaticLabel(
    //   MARITAL_STATUS_OPTIONS,
    //   formData.maritalStatus,
    // ),
    residentialStatusLabel: formData.residentialStatus || "",
    photographLabel: resolveStaticLabel(
      PHOTOGRAPH_OPTIONS,
      formData.photograph,
    ),
    maritalStatusLabel: maritalStatus.label,
    maritalStatusBizCode: maritalStatus.bizCode,
    occupationLabel: occupationType.label,
    occupationTypeLabel: occupationType.label,
    occupationTypeBizCode: occupationType.bizCode,
    incomeProofLabel: incomeProof.label,
    incomeProofBizCode: incomeProof.bizCode,
    addressProofLabel: address.label,
    addrsProofBizCode: address.bizCode,
    ageProofLabel: age.label,
    ageProofBizCode: age.bizCode,
    identificationProofLabel: identification.label,
    identificationBizCode: identification.bizCode,
    stateLabel: formData.state || "",
    cityLabel: formData.city || "",
    communicationStateLabel: formData.communicationState || "",
    communicationCityLabel: formData.communicationCity || "",
    citizenshipLabel: resolveLabel(
      apiCache,
      "countryByName",
      formData.citizenship,
    ),
    citizenshipBizCode: resolveBizCode(
      apiCache,
      "countryByName",
      formData.citizenship,
    ),
    residencyCountryLabel: resolveLabel(
      apiCache,
      "countryByName",
      formData.residencyCountry,
    ),
    residencyCountryBizCode: resolveBizCode(
      apiCache,
      "countryByName",
      formData.residencyCountry,
    ),
    residencyTaxCountryLabel: resolveLabel(
      apiCache,
      "countryByName",
      formData.residencyTaxCountry,
    ),
    residencyTaxCountryBizCode: resolveBizCode(
      apiCache,
      "countryByName",
      formData.residencyTaxCountry,
    ),
  };
};

const getStepPayload = (formData, stepId, apiCache) => {
  if (stepId === "bank") {
    return withStepBizCodes(
      pickKeys(formData, BANK_STEP_KEYS),
      formData,
      stepId,
      apiCache,
    );
  }
  // "personal" owns everything else (basic info, address, documents, declarations).
  const exclude = new Set(BANK_STEP_KEYS);
  const personalPayload = Object.fromEntries(
    Object.entries(formData).filter(([key]) => !exclude.has(key)),
  );
  return withStepBizCodes(personalPayload, formData, stepId, apiCache);
};

const ProposerTab = forwardRef((props, ref) => {
  const {
    onComplete,
    documents,
    setDocuments,
    onUserDataUpdate,
    onPersistStep,
    journeyType,
    activeTab,
  } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState("personal");
  const [completedSteps, setCompletedSteps] = useState([]);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const _stepData = useSelector((state) => state.journey.stepData || {});
  const leadData = useSelector((state) => state.journey.leadFormData);
  const journeyData = useSelector((state) => state.journey?.journeyData);
  const proposerData = useSelector((state) => state.journey.stepData.proposer);
  const annuitantData = useSelector(
    (state) => state.journey.stepData.annuitant,
  );
  const aadharData = useSelector(
    (state) => state.journey.customerVerificationData?.aadhaar?.response,
  );

  const apiCache = useSelector((state) => state.journey.apiCache || {});
  // const cities = apiCache.citydetails?.data || [];
  const allPincodeData = apiCache?.getProductPincodes?.data || [];
  const countries = apiCache.countrydetails?.data || [];
  const hasHydratedInitialLifeAssuredRef = useRef(false);
  const documentSlots = useMemo(
    () => [
      { id: "photograph", label: "Photograph" },
      { id: "ageProof", label: "Age Proof" },
      { id: "addressProof", label: "Address Proof" },
      { id: "identificationProof", label: "Identification Proof" },
      { id: "incomeProof", label: "Income Proof" },
    ],
    [],
  );

  const aadhaarLocation = useMemo(
    () => resolveAadhaarLocation(aadharData?.address, allPincodeData),
    [aadharData?.address, allPincodeData],
  );

  const countryMatch = countries.find(
    (c) =>
      c.countryName?.toLowerCase() ===
      aadharData?.address?.country?.toLowerCase(),
  );

  const SIDE_STEPS = SIDE_STEPS_CONFIG[journeyType] || [];
  const getCompletedStepsFromCurrent = useCallback((stepId) => {
    const idx = SIDE_STEPS.findIndex((s) => s.id === stepId);
    if (idx <= 0) return [];
    return SIDE_STEPS.slice(0, idx).map((s) => s.id);
  }, []);

  const validateFullTab = () => {
    const steps = ["personal", "bank"];

    let isValid = true;
    let firstInvalidStep = null;

    for (let step of steps) {
      const valid = validateStep(step);

      if (!valid) {
        isValid = false;
        if (!firstInvalidStep) firstInvalidStep = step;
      }
    }

    if (!isValid && firstInvalidStep) {
      setCurrentStep(firstInvalidStep);
    }

    return isValid;
  };

  useImperativeHandle(ref, () => ({
    validateTab: validateFullTab,
  }));

  // Aadhaar name is already in Redux after verification — do not wait for state/city master APIs.
  useEffect(() => {
    const fromAadhaar = aadharData?.full_name?.trim();
    const fromLead = leadData?.fullName?.trim();
    const nextName = fromAadhaar || fromLead;
    if (!nextName) return;

    setFormData((prev) => {
      if (prev.fullName?.trim()) return prev;
      return { ...prev, fullName: nextName };
    });
  }, [aadharData?.full_name, leadData?.fullName]);
  const stage8_0 =
    journeyData?.currentStageData?.["Stage 8.0 ProposalFormScreen1"] ||
    journeyData?.stages?.["Stage 8.0 ProposalFormScreen1"] ||
    journeyData?.currentStageData?.["Stage 8.0 Proposal"] ||
    journeyData?.stages?.["Stage 8.0 Proposal"];

  const stage8_1 =
    journeyData?.currentStageData?.["Stage 8.1 ProposalFormScreen2"] ||
    journeyData?.stages?.["Stage 8.1 ProposalFormScreen2"] ||
    journeyData?.currentStageData?.["Stage 8.1 Proposal"] ||
    journeyData?.stages?.["Stage 8.1 Proposal"];

  const mergedStageData = {
    ...mergeProposerStagesFromJourney(journeyData),
    ...(proposerData && typeof proposerData === "object" ? proposerData : {}),
    ...(stage8_0 || {}),
    ...(stage8_1 || {}),
  };

  useEffect(() => {
    if (hasHydratedInitialLifeAssuredRef.current) return;

    if (mergedStageData && Object.keys(mergedStageData).length > 0) {
      const normalized = normalizeProposalBooleanFields(mergedStageData);
      const persistedName = String(normalized.fullName ?? "").trim();
      const [addressLine1, addressLine2, addressLine3] =
        resolveAddressLinesFromSources({
          persisted: normalized,
          aadhaarAddr: aadharData?.address,
          lead: leadData,
        });
      setFormData(
        normalizeProposalAddressFields({
          ...normalized,
          fullName:
            persistedName || aadharData?.full_name || leadData?.fullName || "",
          addressLine1,
          addressLine2,
          addressLine3,
        }),
      );
      hasHydratedInitialLifeAssuredRef.current = true;
      return;
    }
    if (!leadData && !aadharData) return;
    setFormData((prev) => {
      const addr = aadharData?.address;
      const [addressLine1, addressLine2, addressLine3] = packAddressLines([
        prev.addressLine1 ||
        [addr?.house, addr?.street].filter(Boolean).join(" ") ||
        leadData?.addressLine1 ||
        "",
        prev.addressLine2 || addr?.vtc || leadData?.addressLine2 || "",
        prev.addressLine3 ||
        String(addr?.subdist || addr?.dist || addr?.loc || "").trim() ||
        "",
      ]);
      const rawState = String(addr?.state || "").trim();
      const rawCityFallback = String(
        addr?.vtc || addr?.dist || addr?.subdist || leadData?.city || "",
      ).trim();
      const care = aadharData?.care_of || "";
      const normalizedCare = care.trim().toUpperCase();
      let fatherFromCare = "";
      if (
        normalizedCare.startsWith("S/O") ||
        normalizedCare.startsWith("D/O")
      ) {
        fatherFromCare = cleanPersonNamePreservingOverLimit(
          care
            .replace(/^S\/O[:\s-]*/i, "")
            .replace(/^D\/O[:\s-]*/i, "")
            .trim(),
        );
      }
      return normalizeProposalAddressFields({
        ...prev,
        fullName:
          prev.fullName || aadharData?.full_name || leadData?.fullName || "",
        pinCode:
          prev.pinCode || aadharData?.zip || leadData?.residencyPinCode || "",

        fatherName: cleanPersonNamePreservingOverLimit(
          prev.fatherName || fatherFromCare || "",
        ),

        addressLine1,
        addressLine2,
        addressLine3,
        // Show Aadhaar text immediately; master-matched names applied in effect below when APIs load.
        state: prev.state || aadhaarLocation.resolvedState || rawState || "",
        city:
          prev.city || aadhaarLocation.resolvedCity || rawCityFallback || "",
        citizenship:
          prev.citizenship ||
          countryMatch?.countryName ||
          String(addr?.country || "").trim() ||
          "",
      });
    });
    hasHydratedInitialLifeAssuredRef.current = true;
  }, [
    leadData,
    aadharData,
    allPincodeData,
    countries,
    proposerData,
    aadhaarLocation,
    countryMatch,
  ]);
  // After state/city master lists load, replace raw Aadhaar strings with dropdown-aligned names when they still match raw.
  useEffect(() => {
    const addr = aadharData?.address;
    if (!addr || allPincodeData.length === 0) return;

    const loc = resolveAadhaarLocation(addr, allPincodeData);

    if (!loc.resolvedState && !loc.resolvedCity) return;

    const rawState = String(addr.state || "").trim();
    const rawCity = String(addr.vtc || addr.dist || addr.subdist || "").trim();

    setFormData((prev) => {
      const updates = {};

      if (loc.resolvedState) {
        const ps = String(prev.state || "").trim();

        if (
          !ps ||
          normalizeLocationValue(ps) === normalizeLocationValue(rawState)
        ) {
          if (ps !== loc.resolvedState) {
            updates.state = loc.resolvedState;
          }
        }
      }

      if (loc.resolvedCity) {
        const pc = String(prev.city || "").trim();

        if (
          !pc ||
          normalizeLocationValue(pc) === normalizeLocationValue(rawCity)
        ) {
          if (pc !== loc.resolvedCity) {
            updates.city = loc.resolvedCity;
          }
        }
      }

      if (Object.keys(updates).length === 0) return prev;

      return { ...prev, ...updates };
    });
  }, [aadharData?.address, allPincodeData]);

  // When country master loads, map raw Aadhaar country string to master label if citizenship still matches raw.
  useEffect(() => {
    const rawCountry = String(aadharData?.address?.country || "").trim();
    if (!rawCountry || countries.length === 0) return;

    const match = countries.find(
      (c) =>
        normalizeLocationValue(c.countryName) ===
        normalizeLocationValue(rawCountry),
    );
    if (!match?.countryName) return;

    setFormData((prev) => {
      const p = String(prev.citizenship || "").trim();
      if (
        !p ||
        normalizeLocationValue(p) === normalizeLocationValue(rawCountry)
      ) {
        if (p === match.countryName) return prev;
        return { ...prev, citizenship: match.countryName };
      }
      return prev;
    });
  }, [aadharData?.address?.country, countries]);

  useEffect(() => {
    if (location.state?.section) {
      setCurrentStep(location.state.section);
    }
  }, [location.state]);

  // Resume fallback: if section is not in route state, restore from persisted lifeAssured step data.
  useEffect(() => {
    if (location.state?.section) return;
    const persistedStep = proposerData?.stepId;
    if (!persistedStep) return;
    if (!SIDE_STEPS.some((s) => s.id === persistedStep)) return;
    setCurrentStep(persistedStep);
  }, [location.state, proposerData?.stepId]);

  // Rebuild left-side completion dots on refresh/new-tab based on current section.
  useEffect(() => {
    const baseline = getCompletedStepsFromCurrent(currentStep);
    setCompletedSteps((prev) => {
      const merged = [...new Set([...(prev || []), ...baseline])];
      if (merged.length === (prev || []).length) return prev;
      return merged;
    });
  }, [currentStep, getCompletedStepsFromCurrent]);

  const handleChange = useCallback(
    (name, value) => {
      let updatedData;

      if (name === "abhaNo") {
        value = value.replace(/[^0-9]/g, "");
        value = value.slice(0, 14);
      }
      setFormData((prev) => {
        const normalizedValue =
          name === "__BULK__" && value && typeof value === "object"
            ? normalizeProposalBooleanFields(value)
            : BOOLEAN_TOGGLE_FIELDS.has(name)
              ? normalizeBooleanField(value)
              : value;
        if (name === "__BULK__") {
          updatedData = { ...prev, ...normalizedValue };
        } else {
          updatedData = { ...prev, [name]: normalizedValue };
        }

        return updatedData;
      });

      if (name === "__BULK__") {
        dispatch(
          journeyActions.setLeadFormData((prev) => ({
            ...prev,
            ...normalizeProposalBooleanFields(value),
          })),
        );
      } else {
        dispatch(
          journeyActions.setLeadFormData({
            [name]: BOOLEAN_TOGGLE_FIELDS.has(name)
              ? normalizeBooleanField(value)
              : value,
          }),
        );
      }

      if (name === "__BULK__") {
        setErrors((prev) => {
          const newErrors = { ...prev };
          Object.keys(value).forEach((key) => {
            delete newErrors[key];
          });
          return newErrors;
        });
      } else {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    },
    [dispatch],
  );

  const PERSONAL_FIELDS_CONFIG = {
    "nischit-pension": [
      { fieldReference: "title", label: "Title", required: true },
      { fieldReference: "fullName", label: "Full Name", required: true },
      { fieldReference: "fatherName", label: "Father Name", required: true },
      { fieldReference: "motherName", label: "Mother Name", required: true },
      {
        fieldReference: "maritalStatus",
        label: "Marital Status",
        required: true,
      },
      {
        fieldReference: "addressLine1",
        label: "Address Line 1",
        required: true,
      },
      {
        fieldReference: "addressLine2",
        label: "Address Line 2",
        required: true,
      },
      { fieldReference: "pinCode", label: "Pin Code", required: true },
      { fieldReference: "state", label: "State", required: true },
      { fieldReference: "city", label: "City", required: true },
      { fieldReference: "citizenship", label: "Citizenship", required: true },
      { fieldReference: "occupation", label: "Occupation", required: true },
      {
        fieldReference: "jobDiscription",
        label: "Job Discription",
        required: true,
      },
      {
        fieldReference: "residentialStatus",
        label: "Residential Status",
        required: true,
      },
      {
        fieldReference: "abhaNo",
        label: "ABHA Number",
        validation: {
          exactLength: 14,
          errorMessage: "ABHA number must be exactly 14 digits.",
        },
      },
      {
        fieldReference: "communicationAddressSameAsPermanent",
        label: "Communication address same as permanent address",
        required: true,
      },
      {
        fieldReference: "taxResidentialOutsideIndia",
        label: "Resident of Life Assured for Tax purposes in Jurisdiction(s) outside India",
        required: true,
      },
    ],
  };

  const PERSONAL_FIELDS = PERSONAL_FIELDS_CONFIG[journeyType] || [];

  const BANK_FIELDS = [
    {
      fieldReference: "bankName",
      label: "Bank Name",
      validation: { required: true, maxLength: 30 },
    },
    { fieldReference: "branchName", label: "Branch Name", required: true },
    {
      fieldReference: "accountNumber",
      label: "Account Number",
      validation: { required: true, minLength: 9, maxLength: 18 },
    },
    {
      fieldReference: "ifscCode",
      label: "IFSC Code",
      validation: { required: true, regex: "^[A-Z]{4}0[A-Z0-9]{6}$" },
    },
  ];

  const ADDITIONAL_FIELDS = [
    { fieldReference: "photograph", label: "Photograph", required: true },
    { fieldReference: "addressProof", label: "Address Proof", required: true },
    { fieldReference: "ageProof", label: "Age Proof", required: true },
    {
      fieldReference: "incomeProof",
      label: "Financial/Income Documents",
      required: true,
    },
    {
      fieldReference: "identificationProof",
      label: "Identification Proof",
      required: true,
    },
    {
      fieldReference: "identificationNumber",
      label: "Identification Number",
      required: true,
    },
  ];

  const getPersonalFields = () => {
    let fields = [...PERSONAL_FIELDS];

    fields = [...fields, ...getAdditionalFields()];

    fields.push({
      fieldReference: "politicallyExposed",
      label: "Politically Exposed",
      required: true,
    });

    if (formData.communicationAddressSameAsPermanent === false) {
      fields.push(
        {
          fieldReference: "communicationAddressLine1",
          label: "Communication Address Line 1",
          required: true,
        },
        {
          fieldReference: "communicationAddressLine2",
          label: "Communication Address Line 2",
          required: true,
        },
        {
          fieldReference: "communicationPinCode",
          label: "Communication Pin Code",
          required: true,
        },
        {
          fieldReference: "communicationState",
          label: "Communication State",
          required: true,
        },
        {
          fieldReference: "communicationCity",
          label: "Communication City",
          required: true,
        },
        {
          fieldReference: "politicallyExposed",
          label: "Politically Exposed",
          required: true,
        },
      );
    }

    if (formData.taxResidentialOutsideIndia) {
      fields.push(
        {
          fieldReference: "residencyCountry",
          label: "Country",
          required: true,
        },
        {
          fieldReference: "residencyTaxCountry",
          label: "Country of Tax Residency",
          required: true,
        },
        {
          fieldReference: "residencyTaxAddress",
          label: "Address of Tax Residency",
          required: true,
        },
      );
    }

    return fields;
  };

  const getAdditionalFields = () => {
    const fields = [...ADDITIONAL_FIELDS];
    if (formData.politicallyExposed) {
      fields.push({
        fieldReference: "politicallyExposedDescription",
        label: "Politically Exposed Description",
        required: true,
      });
    }
    return fields;
  };

  const STEP_VALIDATORS = {
    personal: (ctx) => ctx.getPersonalFields(),
    bank: () => BANK_FIELDS,
  };

  const validateStep = useCallback(
    (stepId) => {
      const resolver = STEP_VALIDATORS[stepId];
      if (!resolver) return true;

      const fields = resolver({ getPersonalFields, getAdditionalFields });
      const validationErrors = validateForm(formData, fields);

      setErrors(validationErrors);

      return Object.keys(validationErrors).length === 0;
    },
    [formData],
  );

  const handleStepComplete = useCallback(
    async (stepId) => {
      if (!validateStep(stepId)) return;
      if (onPersistStep) {
        setLoading(true);
        try {
          const formDataToSave = normalizeProposalAddressFields(formData);
          const saved = await onPersistStep(`proposer-${stepId}`, {
            tab: "proposer",
            stepId,
            formData: getStepPayload(formDataToSave, stepId, apiCache),
          });
          if (!saved) {
            return;
          }

          setCompletedSteps((prev) =>
            prev.includes(stepId) ? prev : [...prev, stepId],
          );
          const stepIndex = SIDE_STEPS.findIndex((s) => s.id === stepId);
          if (stepIndex < SIDE_STEPS.length - 1) {
            const nextStep = SIDE_STEPS[stepIndex + 1].id;
            setCurrentStep(nextStep);
            navigate("/proposal", {
              state: {
                tab: "proposer",
                section: nextStep,
              },
              replace: true,
            });
          } else {
            // Update user data on completion of all steps
            if (onUserDataUpdate) {
              onUserDataUpdate({
                address:
                  [
                    formDataToSave.addressLine1,
                    formDataToSave.addressLine2,
                    formDataToSave.city,
                    formDataToSave.state,
                  ]
                    .filter(Boolean)
                    .join(", ") +
                  (formDataToSave.pinCode
                    ? ` — ${formDataToSave.pinCode}`
                    : ""),
                accountNumber: formDataToSave.accountNumber
                  ? `XXXX - XXXX - ${formDataToSave.accountNumber.slice(-4)}`
                  : "",
              });
            }
            if (onComplete) onComplete(formDataToSave);
          }
        } finally {
          setLoading(false);
        }
      }
    },
    [formData, onComplete, onUserDataUpdate, onPersistStep],
  );

  const renderSection = () => {
    switch (currentStep) {
      case "personal":
        return (
          <PersonalDetails
            formData={formData}
            errors={errors}
            onChange={handleChange}
            onContinue={() => {
              void handleStepComplete("personal");
            }}
            onUploadClick={() => setShowUploadModal(true)}
            documents={documents}
            journeyType={journeyType}
          />
        );
      case "job":
        return (
          <JobDetails
            formData={formData}
            errors={errors}
            onChange={handleChange}
            setLoading={setLoading}
            onContinue={() => {
              void handleStepComplete("job");
            }}
          />
        );
      case "bank":
        return (
          <BankDetails
            formData={formData}
            errors={errors}
            onChange={handleChange}
            setLoading={setLoading}
            onUploadClick={() => setShowUploadModal(true)}
            documents={documents}
            onBankProofUploaded={(slotMeta) => {
              const updatedDocs = {
                ...(documents || {}),
                proposer: {
                  ...(documents?.proposer || {}),
                  bankAccountProof: slotMeta,
                },
              };
              setDocuments?.(updatedDocs);
              dispatch(journeyActions.setDocuments(updatedDocs));
            }}
            onContinue={() => {
              void handleStepComplete("bank");
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="proposal-tab-layout">
      {loading && (
        <div
          className="quote-premium-loader-overlay"
          aria-live="polite"
          aria-busy="true"
        >
          <BrandLoader />
        </div>
      )}
      <SideProgress
        steps={SIDE_STEPS}
        currentStepId={currentStep}
        completedSteps={completedSteps}
        activeTab={activeTab}
        headingText={proposerHeading}
        paragraphText={proposerParagraphText}
      />

      <div className="proposal-tab-main">

        {renderSection()}
      </div>
      <UploadDocumentsModal
        activeTopTab={activeTab}
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        documentSlots={documentSlots}
        documentFormContext={{
          formData: { ...proposerData, ...annuitantData, ...formData },
          apiCache,
        }}
        initialFiles={documents}
        onUploadComplete={({ files: uploadedFiles, removedSlotIds }) => {
          const updatedDocs = {
            proposer: {
              ...(documents?.proposer || {}),
              ...(uploadedFiles?.proposer || {}),
            },
            primary: {
              ...(documents?.primary || {}),
              ...(uploadedFiles?.primary || {}),
            },
            secondary: {
              ...(documents?.secondary || {}),
              ...(uploadedFiles?.secondary || {}),
            },
          };

          (removedSlotIds || []).forEach(({ tab, slotId }) => {
            if (updatedDocs?.[tab]) {
              delete updatedDocs[tab][slotId];
            }
          });

          setDocuments(updatedDocs);

          dispatch(journeyActions.setDocuments(updatedDocs));
        }}
      />
    </div>
  );
});

export default ProposerTab;
